import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { registrarSesionArchivosApp } from "services/fileSession";
import { captureAppToken } from "services/screenCredentials";
import BloqueoAutogestion from "components/BloqueoAutogestion";
import { GRAPHQL_RECONNECTED_EVENT } from "services/graphqlConnectionEvents";
import { gql, useLazyQuery, useMutation, useQuery, useSubscription } from "@apollo/client";
import {
	Card,
	Checkbox,
	CircularProgress,
	Collapse,
	Divider,
	Fade,
	Grid,
	Icon,
	InputLabel,
	MenuItem,
	Modal,
	Select,
	Tooltip,
} from "@mui/material";
import logo_alt from "assets/images/comedor_alt.png";
import upro_day from "assets/images/Uday2025.png";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import DataTable from "components/DataTable";
import PageLayout from "components/LayoutContainers/EmpleadoLayout";
import CustomPagination from "components/Pagination";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { ConfirmarContext } from "context/ConfirmarContext";
import { UserContext } from "context/user";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getUproColor } from "utils/colors";
import { formatDate, formatMoneyPunto, makeNiceText } from "utils/formatters";
import ModalDetallesPedido from "./components/ModalDetallesPedido";
import ModalNuevoPedido from "./components/ModalNuevoPedido";
import CalendarioMenusSemana from "./components/CalendarioMenusSemana";
import dataPedidos from "./data/dataPedidos";
import ModalValorarPedido from "./components/ModalValorarPedido";
import DataTableCC from "components/Tables/DataTable";
import { valorAPesos } from "utils/formatters";
import ModalCargarPagoCC from "./components/ModalCargarPago";
import MuiLink from "@mui/material/Link";
import SoftInput from "components/SoftInput";
import { MenuContext } from "context/menuContext";
import dataCCInfoTable from "./data/dataCuentaCorrienteInfo";
import { getPagoEstadoVisible, getPagoTitulo } from "./utils/pagoLoteDisplay";
import FacturaImpresion from "layouts/pedidos/components/pedidos/FacturaImpresion";
import upro_receso from "assets/images/Upro_receso.png";
import DialogVouchersActivos from "./components/DialogVouchersActivos";
import { resolveCuentaCorrienteConfig } from "utils/cuentaCorrienteConfig";

const VOUCHERS_ACTIVOS_CLIENTE_QUERY = gql`
  query vouchersActivosClienteSistema(
    $idCliente: Int
    $dni: String
    $idUsuario: Int
    $appUsuario: String
    $fechaOperacion: Date
    $token: String
  ) {
    vouchersActivosCliente(
      idCliente: $idCliente
      dni: $dni
      idUsuario: $idUsuario
      appUsuario: $appUsuario
      fechaOperacion: $fechaOperacion
      token: $token
    ) {
      id
      desde
      hasta
      tipo
      idProducto
      idCategoria
      habilitado
      cantidad
      limiteDiario
      consumido
      consumidoHoy
      saldo
      saldoDiario
      nombreVoucher
      versionNotificacion
      notificacionPendiente
      tipoNovedad
      categoria {
        id
        categoria
      }
      producto { id nombre categoriaProducto { id categoria } }
      grupo {
        id
        nombre
        estado
        idSede
        sede { id nombre }
      }
    }
  }
`;

const RECONOCER_NOTIFICACIONES_VOUCHERS_MUTATION = gql`
  mutation reconocerNotificacionesVouchersSistema(
    $idCliente: Int
    $dni: String
    $idUsuario: Int
    $appUsuario: String
    $notificaciones: [VoucherNotificacionReconocidaInput!]!
    $token: String
  ) {
    reconocerNotificacionesVouchers(
      idCliente: $idCliente
      dni: $dni
      idUsuario: $idUsuario
      appUsuario: $appUsuario
      notificaciones: $notificaciones
      token: $token
    )
  }
`;


const VOUCHERS_CLIENTE_HISTORIAL_QUERY = gql`
  query vouchersClienteHistorialSistema(
    $idCliente: Int
    $dni: String
    $idUsuario: Int
    $appUsuario: String
    $fechaOperacion: Date
    $token: String
  ) {
    vouchersClienteHistorial(
      idCliente: $idCliente
      dni: $dni
      idUsuario: $idUsuario
      appUsuario: $appUsuario
      fechaOperacion: $fechaOperacion
      token: $token
    ) {
      id
      desde
      hasta
      tipo
      idProducto
      cantidad
      limiteDiario
      consumido
      consumidoHoy
      saldo
      saldoDiario
      estadoActual
      nombreVoucher
      categoria {
        id
        categoria
      }
      producto { id nombre categoriaProducto { id categoria } }
      grupo {
        id
        nombre
        estado
        idSede
        sede { id nombre }
      }
    }
  }
`;
const VOUCHER_CONSUMOS_CLIENTE_QUERY = gql`
  query voucherConsumosClienteSistema(
    $filter: JSON
    $page: Int
    $order: Order
    $app: Apps!
    $idUser: ID!
    $dni: String
    $limit: Int
    $token: String
  ) {
    misPedidosPublic(
      filter: $filter
      pagination: { page: $page, limit: $limit }
      order: $order
      app: $app
      idUser: $idUser
      dni: $dni
      token: $token
    ) {
      id
      estado
      fechaPedido
      createdAt
      total
      tipo
      venta {
        id
        idVoucher
        cantidad
        tipo
		subtotal
		descuento
		total
      }
    }
  }
`;

const getVoucherTypeLabel = (tipo) => {
	if (tipo === "MONTO") return "Monto";
	if (tipo === "PRODUCTO") return "Producto";
	if (tipo === "CATEGORIA") return "Categoría";
	return "Voucher";
};

const getVoucherTargetLabel = (voucher) => {
	if (voucher?.tipo === "PRODUCTO") return voucher?.producto?.nombre || "Producto no disponible";
	if (voucher?.tipo === "CATEGORIA") return voucher?.categoria?.categoria || "Categoría no disponible";
	return null;
};

const getVoucherTypeIcon = (tipo) => {
	if (tipo === "MONTO") return "account_balance_wallet";
	if (tipo === "PRODUCTO") return "inventory_2";
	return "category";
};

const REPORTE_CUENTA_CORRIENTE_PERIODO = gql`
  query ReporteCuentaCorrientePorPeriodo(
    $fechaInicio: String!
    $fechaFin: String!
    $tipoCliente: String
    $idCliente: Int
    $idSede: Int
    $token: String
  ) {
    reporteCuentasPorCobrar(
      fechaInicio: $fechaInicio
      fechaFin: $fechaFin
      tipoCliente: $tipoCliente
      idCliente: $idCliente
      idSede: $idSede
      token: $token
    ) {
      totalesPorPeriodo {
        periodo
        totalInicio
        totalDeuda
        totalPagos
        totalBecasRegistradas
        totalBecas
        acumulado
        detallesPorTipo {
          tipoCliente
          totalInicio
          deudas
          pagos
          becas
          becasRegistradas
          acumulado
        }
      }
    }
  }
`;

const LISTADO_PAGOS_CUENTA_CORRIENTE = gql`
  query listadoPagosCuentaCorriente(
    $search: String
    $id: Int
    $idUsuario: Int
    $appUsuario: String
    $token: String
    $filter: JSON
  ) {
    cuentaCorrienteInfo(
      search: $search
      id: $id
      idUsuario: $idUsuario
      appUsuario: $appUsuario
      token: $token
      filter: $filter
    ) {
      __typename
      id
      total
      createdAt
      tipo
      segmentoCuentaCorriente
      esCreditoHistorial
      ... on PagoCuentaCorriente {
        estado
        idLote
        estadoLote
        leyenda
        medioPago
        file
        descripcion
        fechaPago
        motivoCancelacion
        idVenta
        sede {
          id
          nombre
        }
      }
    }
  }
`;

const CUENTA_CORRIENTE_ACTIVITY_QUERY = gql`
  query cuentaCorrienteActivity(
    $search: String
    $id: Int
    $idCliente: Int
    $idUsuario: Int
    $appUsuario: String
    $token: String
    $filter: JSON
  ) {
    cuentaCorrienteInfo(
      search: $search
      id: $id
      idCliente: $idCliente
      idUsuario: $idUsuario
      appUsuario: $appUsuario
      token: $token
      filter: $filter
    ) {
      __typename
      id
      idCliente
      total
      createdAt
      tipo
      estado
      idVenta
      segmentoCuentaCorriente
      ... on CuentaCorriente {
        pagado
        descripcion
        idPedido
        sede {
          id
          nombre
        }
      }
      ... on PagoCuentaCorriente {
        estado
        idLote
        estadoLote
        leyenda
        medioPago
        file
        descripcion
        fechaPago
        motivoCancelacion
        idVenta
        esCreditoHistorial
        sede {
          id
          nombre
        }
      }
    }
  }
`;

const CUENTA_CORRIENTE_RESUMEN_QUERY = gql`
  query cuentaCorrienteResumenSistema(
    $id: Int
    $idUsuario: Int
    $appUsuario: String
    $token: String
    $filterAcumulado: JSON
    $filterMesActual: JSON
  ) {
    acumulado: cuentaCorrienteInfoResumen(
      id: $id
      idUsuario: $idUsuario
      appUsuario: $appUsuario
      token: $token
      filter: $filterAcumulado
    ) {
      totalDeudaPeriodo
      totalPagosAprobadosPeriodo
      totalBecaSaldoRecibidoPeriodo
      totalBecaSaldoAplicadoPeriodo
      saldoPeriodo
      saldoPeriodoNeto
      saldoInicioPeriodo
      saldoInicioPeriodoNeto
      saldoTotal
      saldoTotalNeto
      cantidadPagosPendientesTransferencia
      cantidadPagosRechazadosTransferencia
      saldoCuentaCorrienteReal
      exposicionLimite
      disponibleCuentaCorriente
      reservaSplitPendiente
      limiteCuentaCorriente
      umbralWarning
      umbralError
      umbralErrorCritico
      warning
      error
      errorCritico
      alcanzoTope
      segmentoActualCliente
      mostrarSelectorSegmento
      resumenFuncionario {
        totalDeudaPeriodo
        totalPagosAprobadosPeriodo
        totalBecaSaldoRecibidoPeriodo
        totalBecaSaldoAplicadoPeriodo
        saldoPeriodo
        saldoPeriodoNeto
        saldoInicioPeriodo
        saldoInicioPeriodoNeto
        saldoTotal
        saldoTotalNeto
      }
      resumenNoFuncionario {
        totalDeudaPeriodo
        totalPagosAprobadosPeriodo
        totalBecaSaldoRecibidoPeriodo
        totalBecaSaldoAplicadoPeriodo
        saldoPeriodo
        saldoPeriodoNeto
        saldoInicioPeriodo
        saldoInicioPeriodoNeto
        saldoTotal
        saldoTotalNeto
      }
    }
    mesActual: cuentaCorrienteInfoResumen(
      id: $id
      idUsuario: $idUsuario
      appUsuario: $appUsuario
      token: $token
      filter: $filterMesActual
    ) {
      totalDeudaPeriodo
      totalPagosAprobadosPeriodo
      totalBecaSaldoRecibidoPeriodo
      totalBecaSaldoAplicadoPeriodo
      saldoPeriodo
      saldoPeriodoNeto
      saldoInicioPeriodo
      saldoInicioPeriodoNeto
      saldoTotal
      saldoTotalNeto
      saldoCuentaCorrienteReal
      exposicionLimite
      disponibleCuentaCorriente
      reservaSplitPendiente
      limiteCuentaCorriente
      umbralWarning
      umbralError
      umbralErrorCritico
      warning
      error
      errorCritico
      alcanzoTope
      segmentoActualCliente
      mostrarSelectorSegmento
      resumenFuncionario {
        totalDeudaPeriodo
        totalPagosAprobadosPeriodo
        totalBecaSaldoRecibidoPeriodo
        totalBecaSaldoAplicadoPeriodo
        saldoPeriodo
        saldoPeriodoNeto
        saldoInicioPeriodo
        saldoInicioPeriodoNeto
        saldoTotal
        saldoTotalNeto
      }
      resumenNoFuncionario {
        totalDeudaPeriodo
        totalPagosAprobadosPeriodo
        totalBecaSaldoRecibidoPeriodo
        totalBecaSaldoAplicadoPeriodo
        saldoPeriodo
        saldoPeriodoNeto
        saldoInicioPeriodo
        saldoInicioPeriodoNeto
        saldoTotal
        saldoTotalNeto
      }
    }
  }
`;

const isImageFile = (filename) => {
	if (!filename) return false;
	const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
	const lower = String(filename).toLowerCase();
	return imageExtensions.some((ext) => lower.includes(ext));
};

const isTransferenciaPago = (item) => String(item?.medioPago || "").toLowerCase().includes("transfer");

const formatCuentaCorrienteStatusLabel = (status) => {
	if (!status) return "";
	const normalized = String(status || "").trim().toUpperCase();
	if (normalized === "PENDIENTE DE PAGO") return "Pendiente de pago";
	if (normalized === "PENDIENTE DE COBRO") return "Pendiente de cobro";
	if (normalized === "PAGO EN REVISION" || normalized === "PAGO EN REVISIÓN") {
		return "Pago en revisión";
	}
	return makeNiceText(status);
};

const CUENTA_CORRIENTE_ACTIVITY_PAGE_SIZE = 12;
const CUENTA_CORRIENTE_ACTIVITY_PREVIEW_LIMIT = 3;
const SEGMENTO_FUNCIONARIO = "FUNCIONARIO";
const SEGMENTO_NO_FUNCIONARIO = "NO_FUNCIONARIO";
const CUENTA_CORRIENTE_MONTHS_ES = [
	"enero",
	"febrero",
	"marzo",
	"abril",
	"mayo",
	"junio",
	"julio",
	"agosto",
	"septiembre",
	"octubre",
	"noviembre",
	"diciembre",
];

const formatCuentaCorrienteDayLabel = (dateValue) => {
	const date = dayjs(dateValue);
	if (!date.isValid()) return "Sin fecha";

	if (date.isSame(dayjs(), "day")) return "Hoy";
	if (date.isSame(dayjs().subtract(1, "day"), "day")) return "Ayer";

	const baseLabel = `${date.date()} de ${CUENTA_CORRIENTE_MONTHS_ES[date.month()]}`;
	if (date.year() !== dayjs().year()) {
		return `${baseLabel} de ${date.year()}`;
	}
	return baseLabel;
};

const formatCuentaCorrienteMonthLabel = (dateValue) => {
	const date = dayjs(dateValue);
	if (!date.isValid()) return "";
	return `${CUENTA_CORRIENTE_MONTHS_ES[date.month()]} ${date.year()}`;
};

const getCuentaCorrienteResumenSegmento = (resumen, segmento) => {
	if (!resumen || !segmento) return resumen || null;
	if (segmento === SEGMENTO_FUNCIONARIO) {
		return {
			...(resumen || {}),
			...(resumen?.resumenFuncionario || {}),
		};
	}
	if (segmento === SEGMENTO_NO_FUNCIONARIO) {
		return {
			...(resumen || {}),
			...(resumen?.resumenNoFuncionario || {}),
		};
	}
	return resumen;
};

const getCuentaCorrienteSegmentoLabel = (segmento) =>
	segmento === SEGMENTO_NO_FUNCIONARIO ? "No funcionario" : "Funcionario";

function SistemaComedor() {
	const [showCartel, setShowCartel] = useState(false);
	const [rows, setRows] = useState([]);
	const [rowsCC, setRowsCC] = useState([]);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [datos, setDatos] = useState([]);
	const [datosMobile, setDatosMobile] = useState([]); // Estado específico para datos móviles
	const [pagesInfo, setPagesInfo] = useState({
		pages: 0,
		total: 0,
	});
	const [pagesInfoCC, setPagesInfoCC] = useState({
		pages: 0,
		total: 0,
	});
	const [orderBy, setOrderBy] = useState({
		campo: "id",
		orden: "DESC",
	});
	const [pageCC, setPageCC] = useState(1);
	const [limitCC, setLimitCC] = useState(10);
	const [limitCCMobile, setLimitCCMobile] = useState(3); // Límite específico para móvil
	const [filter, setFilter] = useState();
	const [selectedPedido, setSelectedPedido] = useState(null);
	const [openModalPedidoDetalle, setOpenModalPedidoDetalle] = useState(false);
	const [openModalNuevoPedido, setOpenModalNuevoPedido] = useState(false);
	const [openModalValorarPedido, setOpenModalValorarPedido] = useState(false);
	const [pedidosHoy, setPedidosHoy] = useState([]);
	const [expandedCC, setExpandedCC] = useState(false);
	const [expandedCCResponsive, setExpandedCCResponsive] = useState(false);
	const [expandedSedesMobile, setExpandedSedesMobile] = useState({}); // Estado para controlar qué sedes están expandidas en móvil
	const [limitPerSede, setLimitPerSede] = useState({}); // Estado para controlar límites por sede individual
	const [tieneMultiplesSedes, setTieneMultiplesSedes] = useState(false); // Estado para manejar si el usuario tiene múltiples sedes
	const [modalCargarPagoCC, setOpenModalCargarPagoCC] = useState(false);
	const [token, setToken] = useState(null);
	const [idSede, setIdSede] = useState(localStorage.getItem("idSede") || null);
	const [selectedSedeCC, setSelectedSedeCC] = useState(() => {
		const storedSede = Number(localStorage.getItem("idSede"));
		return Number.isFinite(storedSede) && storedSede > 0 ? storedSede : null;
	});
	const [selectedSegmentoCC, setSelectedSegmentoCC] = useState(null);
	const [pageCCPeriodo, setPageCCPeriodo] = useState(1);
	const { columns, getRows } = dataPedidos;
	const { logout } = useContext(UserContext);
	const { confirmar } = useContext(ConfirmarContext);
	const { menu } = useContext(MenuContext);
	const [openModalFactura, setOpenModalFactura] = useState(false); // Estado para controlar el modal
	const [facturaData, setFacturaData] = useState(null); // Datos de la factura
	const [empresaData, setEmpresaData] = useState(null); // Datos d
	const [expandedPagosCC, setExpandedPagosCC] = useState({});
	const [previewFileCC, setPreviewFileCC] = useState(null);
	const [openVouchersActivosDialog, setOpenVouchersActivosDialog] = useState(false);
	const [voucherAcknowledgementError, setVoucherAcknowledgementError] = useState("");
	const [showCuentaCorrienteActividad, setShowCuentaCorrienteActividad] = useState(false);
	const [showVouchersDetalles, setShowVouchersDetalles] = useState(false);
	const [voucherUnicoDesplegado, setVoucherUnicoDesplegado] = useState(false);
	const [voucherConsumoSeleccionado, setVoucherConsumoSeleccionado] = useState(null);
	const [cuentaCorrienteActivityFilter, setCuentaCorrienteActivityFilter] = useState("todos");
	const [cuentaCorrienteActivityVisibleCount, setCuentaCorrienteActivityVisibleCount] = useState(
		CUENTA_CORRIENTE_ACTIVITY_PAGE_SIZE
	);
	const [cuentaCorrienteActivityScrolled, setCuentaCorrienteActivityScrolled] = useState(false);
	const cuentaCorrienteActivityLoadMoreRef = useRef(null);
	const cuentaCorrienteActivityScrollRef = useRef(null);
	const voucherAcknowledgedSignatureRef = useRef("");
	const ultimoPedidoSyncEventRef = useRef("");
	dayjs.extend(customParseFormat);
	const { columns: columnsCC, columnsCC1, getRows: getRowsCC } = dataCCInfoTable;

	const [decodeUser, { loading, data: usuario }] = useLazyQuery(
		gql`
      query decodeUser($token: String!) {
        decodeUser(token: $token)
      }
    `
	);

	const esFuncionario = Number(usuario?.decodeUser?.cuentaUpro) === 0;
	const fechaCuentaCorrienteHoy = useMemo(() => dayjs().format("YYYY-MM-DD"), []);
	const fechaCuentaCorrienteInicioMesActual = useMemo(
		() => dayjs().startOf("month").format("YYYY-MM-DD"),
		[]
	);
	const segmentoCuentaCorrienteUsuario = useMemo(() => {
		if (Number(usuario?.decodeUser?.cuentaUpro) === 0) return SEGMENTO_FUNCIONARIO;
		if (Number(usuario?.decodeUser?.cuentaUpro) === 1) return SEGMENTO_NO_FUNCIONARIO;
		return null;
	}, [usuario?.decodeUser?.cuentaUpro]);
	const idClienteActual = usuario?.decodeUser?.cliente;

	const { data: responsePago } = useSubscription(gql`
    subscription OnPagoCuenta($idCliente: ID) {
      responseCuenta(idCliente: $idCliente) {
        id
        idCliente
      }
    }
  `, {
		variables: { idCliente: idClienteActual },
		skip: !idClienteActual,
	});

	const { data: nuevoPedidoEvent } = useSubscription(gql`
    subscription OnNuevoPedidoSistemaComedor($idCliente: ID) {
      nuevoPedido(idCliente: $idCliente) {
        id
        updatedAt
      }
    }
  `, {
		variables: { idCliente: idClienteActual },
		skip: !idClienteActual,
	});

	const { data: pedidoModificadoEvent } = useSubscription(gql`
    subscription OnPedidoModificadoSistemaComedor($idCliente: ID) {
      pedidoModificado(idCliente: $idCliente) {
        id
        updatedAt
      }
    }
  `, {
		variables: { idCliente: idClienteActual },
		skip: !idClienteActual,
	});
	const [getPedidos, { loading: loadingPedidos, data }] = useLazyQuery(
		gql`
      query misPedidosPublic(
        $filter: JSON
        $page: Int
        $order: Order
        $app: Apps!
        $idUser: ID!
        $dni: String
        $limit: Int
        $token: String
      ) {
        paginationInfo {
          pages
          total
        }
        misPedidosPublic(
          filter: $filter
          pagination: { page: $page, limit: $limit }
          order: $order
          app: $app
          idUser: $idUser
          dni: $dni
          token: $token
        ) {
          id
          tipo
          estado
          paraRetirar
          total
          createdAt
          updatedAt
          fechaPedido
          cuentaCorriente
          pagado
          horaReserva
          turnoReserva
          valoracion
          destino
          cliente {
            nombre
            apellido
          }
          detalles {
            id
            numeroComanda
            cantidad
            precio
          }
          mesa {
            numero
          }
          venta {
            id
            tipoFactura
            idAnulada
            pagos {
              totalPago
              estado
              medioDePago {
                nombre
              }
            }
          }
          mozo {
            nombre
            apellido
          }
        }
      }
    `,
		{
			variables: {
				filter: filter,
				page: page,
				order: { field: orderBy.campo, order: orderBy.orden },
			},
			fetchPolicy: "no-cache",
			cachePolicy: "no-cache",
		}
	);

	const [getCuentaCorriente, { loading: loadingCC, data: dataCC }] = useLazyQuery(
		gql`
      query cuentasCorrientesToken($token: String!) {
        cuentasCorrientesToken(token: $token) {
          id
          descripcion
          createdAt
          cliente {
            id
            nombre
          }
          total
        }
      }
    `,
		{
			skip: !token,
			fetchPolicy: "no-cache",
			cachePolicy: "no-cache",
		}
	);

	const filtroMovimientosCuentaCorriente = useMemo(
		() => ({
			...(selectedSedeCC ? { sede: Number(selectedSedeCC) } : {}),
			...(selectedSegmentoCC ? { segmentoCuentaCorriente: selectedSegmentoCC } : {}),
		}),
		[selectedSedeCC, selectedSegmentoCC]
	);

	const filtroCuentaCorrienteActividad = useMemo(
		() => ({
			campo: "fechaMovimiento",
			direccion: "DESC",
			fechaInicio: "2000-01-01",
			fechaFin: fechaCuentaCorrienteHoy,
			incluirTodosLosPedidos: true,
			...(selectedSedeCC ? { sede: Number(selectedSedeCC) } : {}),
			...(selectedSegmentoCC ? { segmentoCuentaCorriente: selectedSegmentoCC } : {}),
		}),
		[fechaCuentaCorrienteHoy, selectedSedeCC, selectedSegmentoCC]
	);

	const filtrosResumenCuentaCorriente = useMemo(
		() => ({
			acumulado: {
				fechaInicio: "2000-01-01",
				fechaFin: fechaCuentaCorrienteHoy,
				excluirPedidosConPago: true,
				...(selectedSedeCC ? { sede: Number(selectedSedeCC) } : {}),
			},
			mesActual: {
				fechaInicio: fechaCuentaCorrienteInicioMesActual,
				fechaFin: fechaCuentaCorrienteHoy,
				excluirPedidosConPago: true,
				...(selectedSedeCC ? { sede: Number(selectedSedeCC) } : {}),
			},
		}),
		[
			fechaCuentaCorrienteHoy,
			fechaCuentaCorrienteInicioMesActual,
			selectedSedeCC,
		]
	);

	const {
		data: dataCCInfo,
		loading: loadingCCInfo,
		refetch: refetchCC,
	} = useQuery(
		gql`
      query cuentaCorrienteInfo(
        $pagination: PaginationInput
        $search: String
        $id: Int
        $idUsuario: Int
        $appUsuario: String
        $token: String
        $filter: JSON
      ) {
        paginationInfo {
          pages
          total
        }
        cuentaCorrienteInfo(
          pagination: $pagination
          search: $search
          id: $id
          idUsuario: $idUsuario
          appUsuario: $appUsuario
          token: $token
          filter: $filter
        ) {
          id
          idCliente
          total
          createdAt
          tipo
          idVenta
          segmentoCuentaCorriente
          ... on CuentaCorriente {
            __typename
            pagado
            descripcion
            sede {
              id
              nombre
            }
          }
          ... on PagoCuentaCorriente {
            __typename
            estado
            idLote
            estadoLote
            leyenda
            medioPago
            motivoCancelacion
            file
            idVenta
            descripcion
            sede {
              id
              nombre
            }
          }
        }
      }
    `,
		{
			variables: {
				pagination: { page: pageCC, limit: limitCC },
				id: Number(usuario?.decodeUser?.dni),
				idUsuario: Number(usuario?.decodeUser?.id || 0) || null,
				appUsuario: usuario?.decodeUser?.sistema || null,
				token: token,
				filter: filtroMovimientosCuentaCorriente,
			},
			skip: !usuario?.decodeUser?.dni || !token,
			fetchPolicy: "no-cache",
			cachePolicy: "no-cache",
		}
	);

	const {
		data: dataPagosCCListado,
		loading: loadingPagosCCListado,
		refetch: refetchPagosCCListado,
	} = useQuery(LISTADO_PAGOS_CUENTA_CORRIENTE, {
		variables: {
			id: Number(usuario?.decodeUser?.dni),
			idUsuario: Number(usuario?.decodeUser?.id || 0) || null,
			appUsuario: usuario?.decodeUser?.sistema || null,
			token: token,
			filter: { ...filtroMovimientosCuentaCorriente, tipo: "PagoCuentaCorriente" },
		},
		skip: !usuario?.decodeUser?.dni || !token,
		fetchPolicy: "no-cache",
		cachePolicy: "no-cache",
	});

	const {
		data: dataCuentaCorrienteActivity,
		loading: loadingCuentaCorrienteActivity,
		refetch: refetchCuentaCorrienteActivity,
	} = useQuery(CUENTA_CORRIENTE_ACTIVITY_QUERY, {
		variables: {
			id: Number(usuario?.decodeUser?.dni),
			idCliente: Number(usuario?.decodeUser?.cliente || 0) || null,
			idUsuario: Number(usuario?.decodeUser?.id || 0) || null,
			appUsuario: usuario?.decodeUser?.sistema || null,
			token: token,
			filter: filtroCuentaCorrienteActividad,
		},
		skip: !usuario?.decodeUser?.dni || !token,
		fetchPolicy: "no-cache",
		cachePolicy: "no-cache",
	});

	// Query separada para obtener información de sedes
	const {
		data: dataVouchersActivosCliente,
		loading: loadingVouchersActivosCliente,
		refetch: refetchVouchersActivosCliente,
	} = useQuery(VOUCHERS_ACTIVOS_CLIENTE_QUERY, {
		variables: {
			idCliente: Number(usuario?.decodeUser?.cliente || 0) || null,
			dni: String(usuario?.decodeUser?.dni || ""),
			idUsuario: Number(usuario?.decodeUser?.id || 0) || null,
			appUsuario: usuario?.decodeUser?.sistema || null,
			fechaOperacion: dayjs().format("YYYY-MM-DD"),
			token,
		},
		skip: !usuario?.decodeUser?.dni || !token,
		fetchPolicy: "no-cache",
		cachePolicy: "no-cache",
	});
	const [
		reconocerNotificacionesVouchers,
		{ loading: reconociendoNotificacionesVouchers },
	] = useMutation(RECONOCER_NOTIFICACIONES_VOUCHERS_MUTATION);

	const {
		data: dataVouchersClienteHistorial,
		loading: loadingVouchersClienteHistorial,
		refetch: refetchVouchersClienteHistorial,
	} = useQuery(VOUCHERS_CLIENTE_HISTORIAL_QUERY, {
		variables: {
			idCliente: Number(usuario?.decodeUser?.cliente || 0) || null,
			dni: String(usuario?.decodeUser?.dni || ""),
			idUsuario: Number(usuario?.decodeUser?.id || 0) || null,
			appUsuario: usuario?.decodeUser?.sistema || null,
			fechaOperacion: dayjs().format("YYYY-MM-DD"),
			token,
		},
		skip: !usuario?.decodeUser?.dni || !token,
		fetchPolicy: "no-cache",
		cachePolicy: "no-cache",
	});

	const {
		data: dataVoucherConsumosCliente,
		loading: loadingVoucherConsumosCliente,
		error: errorVoucherConsumosCliente,
	} = useQuery(VOUCHER_CONSUMOS_CLIENTE_QUERY, {
		variables: {
			filter: null,
			page: 1,
			order: { field: "fechaPedido", order: "DESC" },
			app: usuario?.decodeUser?.sistema,
			idUser: usuario?.decodeUser?.id,
			dni: String(usuario?.decodeUser?.dni || ""),
			limit: 5000,
			token,
		},
		skip:
			!voucherConsumoSeleccionado?.voucher?.id ||
			!usuario?.decodeUser?.id ||
			!usuario?.decodeUser?.sistema,
		fetchPolicy: "no-cache",
		cachePolicy: "no-cache",
	});

	const vouchersClienteHistorial = dataVouchersClienteHistorial?.vouchersClienteHistorial || [];
	const vouchersDisponiblesCliente = vouchersClienteHistorial.filter((voucher) => voucher?.estadoActual === "VIGENTE");
	const vouchersNoDisponiblesCliente = vouchersClienteHistorial.filter((voucher) => voucher?.estadoActual === "NO_DISPONIBLE");
	const vouchersHistorialCliente = vouchersClienteHistorial.filter((voucher) =>
		["VENCIDO", "SIN_SALDO"].includes(voucher?.estadoActual)
	);
	const vouchersVisiblesCliente = vouchersClienteHistorial.filter((voucher) => voucher?.estadoActual !== "PROXIMO");

	const vouchersActivosCliente = useMemo(
		() => dataVouchersActivosCliente?.vouchersActivosCliente || [],
		[dataVouchersActivosCliente?.vouchersActivosCliente]
	);
	const vouchersConNotificacionPendiente = useMemo(
		() => vouchersActivosCliente.filter((voucher) => voucher?.notificacionPendiente),
		[vouchersActivosCliente]
	);
	const voucherNotificationSignature = useMemo(
		() =>
			vouchersConNotificacionPendiente
				.map((voucher) => `${voucher.id}:${voucher.versionNotificacion}`)
				.sort()
				.join("|"),
		[vouchersConNotificacionPendiente]
	);
	const canShowVouchersActivosDialog =
		!showCartel &&
		Boolean(idSede) &&
		Boolean(usuario) &&
		!loading &&
		!loadingPedidos &&
		!loadingCCInfo &&
		!loadingVouchersActivosCliente;

	const { data: dataSedesInfo, loading: loadingSedesInfo } = useQuery(
		gql`
      query sedesCuentaCorrienteInfo(
        $search: String
        $id: Int
        $idUsuario: Int
        $appUsuario: String
        $token: String
      ) {
        sedesCuentaCorrienteInfo(
          search: $search
          id: $id
          idUsuario: $idUsuario
          appUsuario: $appUsuario
          token: $token
        ) {
          tieneMultiplesSedes
          totalSedes
          sedes {
            id
            nombre
          }
        }
      }
    `,
		{
			variables: {
				id: Number(usuario?.decodeUser?.dni),
				idUsuario: Number(usuario?.decodeUser?.id || 0) || null,
				appUsuario: usuario?.decodeUser?.sistema || null,
				token: token,
			},
			skip: !usuario?.decodeUser?.dni || !token,
			fetchPolicy: "no-cache",
			cachePolicy: "no-cache",
		}
	);

	// Query separada para cuenta corriente en móvil
	const {
		data: dataCCInfoMobile,
		loading: loadingCCInfoMobile,
		refetch: refetchCCMobile,
	} = useQuery(
		gql`
      query cuentaCorrienteInfoMobile(
        $pagination: PaginationInput
        $search: String
        $id: Int
        $idUsuario: Int
        $appUsuario: String
        $token: String
        $filter: JSON
      ) {
        paginationInfo {
          pages
          total
        }
        cuentaCorrienteInfo(
          pagination: $pagination
          search: $search
          id: $id
          idUsuario: $idUsuario
          appUsuario: $appUsuario
          token: $token
          filter: $filter
        ) {
          id
          idCliente
          total
          createdAt
          tipo
          idVenta
          segmentoCuentaCorriente
          ... on CuentaCorriente {
            __typename
            pagado
            descripcion
            sede {
              id
              nombre
            }
          }
          ... on PagoCuentaCorriente {
            __typename
            estado
            idLote
            estadoLote
            leyenda
            medioPago
            motivoCancelacion
            file
            idVenta
            descripcion
            sede {
              id
              nombre
            }
          }
        }
      }
    `,
		{
			variables: {
				pagination: { page: 1, limit: limitCCMobile },
				id: Number(usuario?.decodeUser?.dni),
				idUsuario: Number(usuario?.decodeUser?.id || 0) || null,
				appUsuario: usuario?.decodeUser?.sistema || null,
				token: token,
				filter: filtroMovimientosCuentaCorriente,
			},
			skip: !usuario?.decodeUser?.dni || !token,
			fetchPolicy: "no-cache",
			cachePolicy: "no-cache",
		}
	);

	const [getVenta] = useLazyQuery(
		gql`
      query getVenta($id: ID!, $token: String!) {
        venta(id: $id, token: $token) {
          id
          idAnulada
          idPedido
          cliente {
            nombre
            apellido
            appUsuario
          }
          tipoFactura
          fechaFactura
          tipo_cbte
          tipo_doc
          nro_doc
          imp_iva21
          subtotal21
          concepto
          nroAnulado
          url
          pto_vta
          numeroDeFacturaAfip
          CAE
          CAEFchVto
          descuento
          recargo
          total
          mesa
          pedido {
            id
            tipo
            cuentaCorriente
            propina
            detalles {
              producto {
                nombre
              }
              cantidad
              precio
            }
          }
        }
      }
    `
	);

	const [getPrecioMenu, { data: dataPrecioParticular, loading: loadingPrecioParticular }] =
		useLazyQuery(
			gql`
        query getPrecioParticular($idSede: ID!, $tipoUsuario: String) {
          preciosMenuLibre(idSede: $idSede, tipoUsuario: $tipoUsuario)
        }
      `
		);

	// Función para agrupar datos por sede
	const groupBySede = (data) => {
		if (!data || data.length === 0) return {};

		return data.reduce((acc, item) => {
			const sedeKey = item.sede?.nombre || "Sin sede asignada";
			if (!acc[sedeKey]) {
				acc[sedeKey] = [];
			}
			acc[sedeKey].push(item);
			return acc;
		}, {});
	};

	// Función para manejar la expansión de sedes en móvil
	const toggleSedeMobile = (sedeNombre) => {
		setExpandedSedesMobile((prev) => ({
			...prev,
			[sedeNombre]: !prev[sedeNombre],
		}));
	};

	// Función para obtener el límite de una sede específica
	const getLimitForSede = (sedeNombre) => {
		return limitPerSede[sedeNombre] || 3;
	};

	// Función para incrementar el límite de una sede específica
	const incrementLimitForSede = (sedeNombre) => {
		setLimitPerSede((prev) => ({
			...prev,
			[sedeNombre]: (prev[sedeNombre] || 3) + 3,
		}));
	};

	// Función para cargar más registros en móvil
	const handleLoadMoreMobile = () => {
		setLimitCCMobile(prevLimit => prevLimit + 3);
	};

	const { data: dataSedes, loading: loadingSedes } = useQuery(
		gql`
      query sedesToken($token: String!) {
        sedesToken(token: $token) {
          id
          nombre
        }
      }
    `,
		{
			variables: {
				token: token,
			},
			skip: !token,
			fetchPolicy: "no-cache",
			cachePolicy: "no-cache",
		}
	);

	const {
		data: dataCuentaCorrienteResumen,
		loading: loadingCuentaCorrienteResumen,
		refetch: refetchCuentaCorrienteResumen,
	} = useQuery(CUENTA_CORRIENTE_RESUMEN_QUERY, {
		variables: {
			id: Number(usuario?.decodeUser?.dni),
			idUsuario: Number(usuario?.decodeUser?.id || 0) || null,
			appUsuario: usuario?.decodeUser?.sistema || null,
			token,
			filterAcumulado: filtrosResumenCuentaCorriente.acumulado,
			filterMesActual: filtrosResumenCuentaCorriente.mesActual,
		},
		skip: !usuario?.decodeUser?.dni || !token,
		fetchPolicy: "no-cache",
		cachePolicy: "no-cache",
	});

	const sedesCuentaCorriente = useMemo(() => {
		const sedesConMovimientos = dataSedesInfo?.sedesCuentaCorrienteInfo?.sedes;
		if (Array.isArray(sedesConMovimientos) && sedesConMovimientos.length > 0) {
			return sedesConMovimientos;
		}
		return dataSedes?.sedesToken || [];
	}, [dataSedesInfo, dataSedes]);

	const resumenCuentaCorrienteAcumulado = dataCuentaCorrienteResumen?.acumulado || null;
	const resumenCuentaCorrienteMesActual = dataCuentaCorrienteResumen?.mesActual || null;
	const segmentoCuentaCorrienteActual =
		resumenCuentaCorrienteAcumulado?.segmentoActualCliente || segmentoCuentaCorrienteUsuario;
	const mostrarSelectorSegmentoCuentaCorriente = Boolean(
		resumenCuentaCorrienteAcumulado?.mostrarSelectorSegmento
	);
	const segmentoCuentaCorrienteSeleccionado =
		selectedSegmentoCC || segmentoCuentaCorrienteActual || null;
	const resumenCuentaCorrienteAcumuladoSeleccionado = useMemo(
		() =>
			getCuentaCorrienteResumenSegmento(
				resumenCuentaCorrienteAcumulado,
				segmentoCuentaCorrienteSeleccionado
			),
		[resumenCuentaCorrienteAcumulado, segmentoCuentaCorrienteSeleccionado]
	);
	const resumenCuentaCorrienteMesActualSeleccionado = useMemo(
		() =>
			getCuentaCorrienteResumenSegmento(
				resumenCuentaCorrienteMesActual,
				segmentoCuentaCorrienteSeleccionado
			),
		[resumenCuentaCorrienteMesActual, segmentoCuentaCorrienteSeleccionado]
	);
	const segmentoCuentaCorrienteBloqueaNuevoPedido =
		Boolean(segmentoCuentaCorrienteActual) &&
		Boolean(segmentoCuentaCorrienteSeleccionado) &&
		segmentoCuentaCorrienteActual !== segmentoCuentaCorrienteSeleccionado;
	const puedeCargarPagoCuentaCorriente =
		esFuncionario &&
		segmentoCuentaCorrienteActual === SEGMENTO_FUNCIONARIO &&
		segmentoCuentaCorrienteSeleccionado === SEGMENTO_FUNCIONARIO;

	const idClienteCuentaCorriente = Number(
		dataCCInfo?.cuentaCorrienteInfo?.[0]?.idCliente || usuario?.decodeUser?.cliente || 0
	);
	const tipoClienteReporteCC = useMemo(() => {
		if (segmentoCuentaCorrienteSeleccionado === SEGMENTO_FUNCIONARIO) {
			return "Funcionario";
		}
		if (segmentoCuentaCorrienteSeleccionado === SEGMENTO_NO_FUNCIONARIO) {
			return "No Funcionario";
		}
		return esFuncionario ? "Funcionario" : null;
	}, [esFuncionario, segmentoCuentaCorrienteSeleccionado]);

	const shouldFetchReporteCCPeriodo =
		Boolean(token) &&
		Boolean(usuario?.decodeUser?.habilitadoCuentaCorriente) &&
		Boolean(idClienteCuentaCorriente) &&
		(!tieneMultiplesSedes || Boolean(selectedSedeCC));

	const {
		data: dataReporteCCPeriodo,
		loading: loadingReporteCCPeriodo,
		refetch: refetchReporteCCPeriodo,
	} = useQuery(REPORTE_CUENTA_CORRIENTE_PERIODO, {
		variables: {
			fechaInicio: "2000-01-01",
			fechaFin: dayjs().format("YYYY-MM-DD"),
			tipoCliente: tipoClienteReporteCC,
			idCliente: idClienteCuentaCorriente || null,
			idSede: selectedSedeCC || null,
			token: token,
		},
		skip: !shouldFetchReporteCCPeriodo,
		fetchPolicy: "no-cache",
		cachePolicy: "no-cache",
	});

	const rowsCCPeriodo = useMemo(() => {
		const rows = dataReporteCCPeriodo?.reporteCuentasPorCobrar?.totalesPorPeriodo || [];
		return rows
			.map((row) => {
				const tiposPermitidos =
					segmentoCuentaCorrienteSeleccionado === SEGMENTO_FUNCIONARIO
						? ["Funcionario"]
						: segmentoCuentaCorrienteSeleccionado === SEGMENTO_NO_FUNCIONARIO
							? ["No Funcionario"]
							: esFuncionario
								? ["Funcionario"]
								: ["Funcionario", "No Funcionario"];
				const detallesComedor = (row?.detallesPorTipo || []).filter(
					(detalle) =>
						tiposPermitidos.includes(detalle?.tipoCliente)
				);

				const sumField = (field) =>
					detallesComedor.reduce((acc, detalle) => acc + Number(detalle?.[field] || 0), 0);

				if (!detallesComedor.length) {
					return {
						periodo: row?.periodo,
						totalInicio: Number(row?.totalInicio || 0),
						totalDeuda: Number(row?.totalDeuda || 0),
						totalPagos: Number(row?.totalPagos || 0),
						totalBecasRegistradas: Number(row?.totalBecasRegistradas || 0),
						totalBecas: Number(row?.totalBecas || 0),
						acumulado: Number(row?.acumulado || 0),
					};
				}

				return {
					periodo: row?.periodo,
					totalInicio: sumField("totalInicio"),
					totalDeuda: sumField("deudas"),
					totalPagos: sumField("pagos"),
					totalBecasRegistradas: sumField("becasRegistradas"),
					totalBecas: sumField("becas"),
					acumulado: sumField("acumulado"),
				};
			})
			.filter((row) => row?.periodo);
	}, [dataReporteCCPeriodo, esFuncionario, segmentoCuentaCorrienteSeleccionado]);
	const limitCCPeriodo = 6;
	const pagesCCPeriodo = Math.max(1, Math.ceil(rowsCCPeriodo.length / limitCCPeriodo));
	const rowsCCPeriodoVisible = rowsCCPeriodo.slice(
		(pageCCPeriodo - 1) * limitCCPeriodo,
		pageCCPeriodo * limitCCPeriodo
	);
	useEffect(() => {
		if (dataCCInfo) {
			setDatos(dataCCInfo?.cuentaCorrienteInfo);
			setPagesInfoCC({
				pages: dataCCInfo?.paginationInfo?.pages,
				total: dataCCInfo?.paginationInfo?.total,
			});
			setRowsCC(getRowsCC(dataCCInfo?.cuentaCorrienteInfo, handleOpenMenu));
		}
	}, [dataCCInfo, dataCC]);

	// useEffect separado para datos móviles
	useEffect(() => {
		if (dataCCInfoMobile) {
			setDatosMobile(dataCCInfoMobile?.cuentaCorrienteInfo);
		}
	}, [dataCCInfoMobile]);

	// useEffect para refrescar datos móviles cuando cambia el límite
	useEffect(() => {
		if (limitCCMobile > 3) {
			refetchCCMobile();
		}
	}, [limitCCMobile]);

	// useEffect separado para manejar la información de sedes
	useEffect(() => {
		if (dataSedesInfo?.sedesCuentaCorrienteInfo) {
			setTieneMultiplesSedes(dataSedesInfo.sedesCuentaCorrienteInfo.tieneMultiplesSedes);
		}
	}, [dataSedesInfo]);

	useEffect(() => {
		if (!sedesCuentaCorriente.length) return;
		const existsSelected = sedesCuentaCorriente.some(
			(sede) => Number(sede?.id) === Number(selectedSedeCC)
		);
		if (existsSelected) return;

		const storedSede = Number(localStorage.getItem("idSede"));
		const storedSedeValida = sedesCuentaCorriente.find(
			(sede) => Number(sede?.id) === storedSede
		);
		const fallbackSede = Number(storedSedeValida?.id || sedesCuentaCorriente?.[0]?.id || 0);
		if (fallbackSede > 0) {
			setSelectedSedeCC(fallbackSede);
		}
	}, [sedesCuentaCorriente, selectedSedeCC]);

	useEffect(() => {
		if (!segmentoCuentaCorrienteActual) return;
		setSelectedSegmentoCC((prev) => {
			if (!mostrarSelectorSegmentoCuentaCorriente) {
				return segmentoCuentaCorrienteActual;
			}
			if (prev === SEGMENTO_FUNCIONARIO || prev === SEGMENTO_NO_FUNCIONARIO) {
				return prev;
			}
			return segmentoCuentaCorrienteActual;
		});
	}, [mostrarSelectorSegmentoCuentaCorriente, segmentoCuentaCorrienteActual]);

	useEffect(() => {
		setPageCCPeriodo(1);
	}, [selectedSedeCC, selectedSegmentoCC, rowsCCPeriodo.length]);

	const saldoCuentaCorrienteUnificado = useMemo(() => {
		return Number(resumenCuentaCorrienteAcumuladoSeleccionado?.saldoTotalNeto || 0);
	}, [resumenCuentaCorrienteAcumuladoSeleccionado]);
	const configuracionCuentaCorrienteActual = useMemo(
		() => resolveCuentaCorrienteConfig(resumenCuentaCorrienteAcumuladoSeleccionado),
		[resumenCuentaCorrienteAcumuladoSeleccionado]
	);
	const {
		limiteCuentaCorriente: limiteCuentaCorrienteActual,
		umbralWarning: umbralWarningCuentaCorrienteActual,
		umbralError: umbralErrorCuentaCorrienteActual,
	} = configuracionCuentaCorrienteActual;
	const saldoCuentaCorrientePendienteActual = useMemo(
		() =>
			Number(
				resumenCuentaCorrienteAcumuladoSeleccionado?.exposicionLimite ||
				Math.max(Number(-saldoCuentaCorrienteUnificado || 0), 0)
			),
		[resumenCuentaCorrienteAcumuladoSeleccionado, saldoCuentaCorrienteUnificado]
	);
	const disponibleCuentaCorrienteActual = useMemo(
		() =>
			Number(
				resumenCuentaCorrienteAcumuladoSeleccionado?.disponibleCuentaCorriente ??
				Math.max(
					Number(limiteCuentaCorrienteActual || 0) -
					Number(saldoCuentaCorrientePendienteActual || 0),
					0
				)
			),
		[
			limiteCuentaCorrienteActual,
			resumenCuentaCorrienteAcumuladoSeleccionado,
			saldoCuentaCorrientePendienteActual,
		]
	);
	const bloqueoCuentaCorrienteLimite = useMemo(
		() =>
			saldoCuentaCorrientePendienteActual >= limiteCuentaCorrienteActual,
		[limiteCuentaCorrienteActual, saldoCuentaCorrientePendienteActual]
	);
	const mensajeAlertaCuentaCorrienteCard = useMemo(() => {
		const disponibleTexto = `$${formatMoneyPunto(disponibleCuentaCorrienteActual)}`;
		const limiteTexto = `$${formatMoneyPunto(limiteCuentaCorrienteActual)}`;

		if (saldoCuentaCorrientePendienteActual >= limiteCuentaCorrienteActual) {
			return `Límite de ${limiteTexto} alcanzado. Regulariza tu deuda para restablecer el saldo disponible`;
		}

		if (saldoCuentaCorrientePendienteActual >= umbralErrorCuentaCorrienteActual) {
			return `Alerta de consumo: Próximo a alcanzar el límite de ${limiteTexto}. Disponible ${disponibleTexto}`;
		}

		if (saldoCuentaCorrientePendienteActual >= umbralWarningCuentaCorrienteActual) {
			return `Recuerda que el límite máximo de consumo es de ${limiteTexto}. Disponible ${disponibleTexto}`;
		}

		return `Límite disponible en cuenta corriente: ${disponibleTexto}`;
	}, [
		disponibleCuentaCorrienteActual,
		limiteCuentaCorrienteActual,
		saldoCuentaCorrientePendienteActual,
		umbralErrorCuentaCorrienteActual,
		umbralWarningCuentaCorrienteActual,
	]);
	const alertaCuentaCorrienteCard = useMemo(() => {
		const limiteTexto = `$${formatMoneyPunto(limiteCuentaCorrienteActual)}`;

		if (saldoCuentaCorrientePendienteActual >= limiteCuentaCorrienteActual) {
			return {
				icon: "gpp_bad",
				accent: "#FFFFFF",
				border: "rgba(127, 29, 29, 0.5)",
				background:
					"linear-gradient(135deg, rgba(127, 29, 29, 0.98) 0%, rgba(153, 27, 27, 0.96) 100%)",
				iconBg: "rgba(255, 255, 255, 0.16)",
				message: `Límite de ${limiteTexto} alcanzado. Regulariza tu deuda para restablecer el saldo disponible`,
			};
		}

		if (saldoCuentaCorrientePendienteActual >= umbralErrorCuentaCorrienteActual) {
			return {
				icon: "report_gmailerrorred",
				accent: "#B91C1C",
				border: "rgba(239, 68, 68, 0.22)",
				background: "rgba(254, 242, 242, 0.98)",
				iconBg: "rgba(239, 68, 68, 0.12)",
				message: `Alerta de consumo: Próximo a alcanzar el límite de ${limiteTexto}`,
			};
		}

		if (saldoCuentaCorrientePendienteActual >= umbralWarningCuentaCorrienteActual) {
			return {
				icon: "warning_amber",
				accent: "#B45309",
				border: "rgba(245, 158, 11, 0.24)",
				background: "rgba(255, 251, 235, 0.98)",
				iconBg: "rgba(245, 158, 11, 0.14)",
				message: `Recuerda que el límite máximo de consumo es de ${limiteTexto}`,
			};
		}

		return {
			icon: "verified",
			accent: "#15803D",
			border: "rgba(34, 197, 94, 0.18)",
			background: "rgba(240, 253, 244, 0.98)",
			iconBg: "rgba(34, 197, 94, 0.12)",
			message: `Límite disponible en cuenta corriente: ${limiteTexto}`,
		};
	}, [
		limiteCuentaCorrienteActual,
		saldoCuentaCorrientePendienteActual,
		umbralErrorCuentaCorrienteActual,
		umbralWarningCuentaCorrienteActual,
	]);

	const pagosCuentaCorriente = useMemo(() => {
		const rows = dataPagosCCListado?.cuentaCorrienteInfo || [];
		return rows
			.filter((item) => {
				if (item?.tipo !== "PagoCuentaCorriente" && item?.__typename !== "PagoCuentaCorriente") return false;
				if (item?.estado === "Pendiente" || item?.estado === "Rechazado") {
					return isTransferenciaPago(item);
				}
				return true;
			})
			.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
	}, [dataPagosCCListado]);

	const pagosCuentaCorrienteMobile = useMemo(
		() => pagosCuentaCorriente.slice(0, limitCCMobile),
		[pagosCuentaCorriente, limitCCMobile]
	);

	const cantidadPagosPendientes = useMemo(
		() => pagosCuentaCorriente.filter((item) => item?.estado === "Pendiente" && isTransferenciaPago(item)).length,
		[pagosCuentaCorriente]
	);

	const cantidadPagosRechazados = useMemo(
		() => pagosCuentaCorriente.filter((item) => item?.estado === "Rechazado" && isTransferenciaPago(item)).length,
		[pagosCuentaCorriente]
	);

	const cuentaCorrienteActivityItems = useMemo(() => {
		const movimientos = dataCuentaCorrienteActivity?.cuentaCorrienteInfo || [];
		const pagosFiltrados = movimientos.filter((item) => {
			const esPago =
				item?.tipo === "PagoCuentaCorriente" || item?.__typename === "PagoCuentaCorriente";
			if (!esPago) return true;
			if (item?.estado === "Pendiente" || item?.estado === "Rechazado") {
				return isTransferenciaPago(item);
			}
			return true;
		});

		return pagosFiltrados
			.map((item) => {
				const esPago =
					item?.tipo === "PagoCuentaCorriente" || item?.__typename === "PagoCuentaCorriente";
				const esPedido = item?.tipo === "CuentaCorriente" || item?.__typename === "CuentaCorriente";
				const pedido = esPedido
					? {
						id: Number(item?.idPedido || item?.id || 0),
					}
					: null;

				if (!esPago && !esPedido) return null;
				if (selectedSedeCC && Number(item?.sede?.id) !== Number(selectedSedeCC)) return null;
				if (esPedido && !Number(item?.idPedido || 0)) return null;

				const fechaMovimiento = esPago
					? item?.fechaPago || item?.createdAt
					: item?.createdAt;
				const montoBase = Number(item?.total ?? 0);
				const montoAbsoluto = Math.abs(montoBase);
				const fecha = dayjs(fechaMovimiento);

				return {
					key: `${esPago ? "pago" : "pedido"}-${item?.id}`,
					id: esPago ? Number(item?.id || 0) : Number(item?.idPedido || pedido?.id || item?.id || 0),
					tipo: esPago ? "pago" : "pedido",
					label: esPago ? getPagoTitulo(item) : "Pedido",
					subtitle: esPago
						? item?.medioPago || "Pago en cuenta corriente"
						: item?.descripcion || "Pedido en cuenta corriente",
					estado: item?.estado,
					estadoVisible: esPago ? getPagoEstadoVisible(item) : item?.estado,
					amount: esPago ? montoAbsoluto : montoAbsoluto * -1,
					amountAbs: montoAbsoluto,
					movementDate: fechaMovimiento,
					sortValue: fecha.isValid() ? fecha.valueOf() : 0,
					dateKey: fecha.isValid() ? fecha.format("YYYY-MM-DD") : "sin-fecha",
					dateLabel: formatCuentaCorrienteDayLabel(fechaMovimiento),
					pedido,
					medioPago: item?.medioPago,
					descripcion: item?.descripcion,
					motivoCancelacion: item?.motivoCancelacion,
					file: item?.file,
					sede: item?.sede,
					raw: item,
				};
			})
			.filter(Boolean);
	}, [dataCuentaCorrienteActivity, selectedSedeCC]);

	const cuentaCorrienteActivityFilteredItems = useMemo(() => {
		if (cuentaCorrienteActivityFilter === "pedidos") {
			return cuentaCorrienteActivityItems.filter((item) => item.tipo === "pedido");
		}

		if (cuentaCorrienteActivityFilter === "pagos") {
			return cuentaCorrienteActivityItems.filter((item) => item.tipo === "pago");
		}

		return cuentaCorrienteActivityItems;
	}, [cuentaCorrienteActivityFilter, cuentaCorrienteActivityItems]);

	const cuentaCorrienteActivityPreviewItems = useMemo(
		() =>
			cuentaCorrienteActivityItems.slice(0, CUENTA_CORRIENTE_ACTIVITY_PREVIEW_LIMIT),
		[cuentaCorrienteActivityItems]
	);

	const cuentaCorrienteActivityVisibleItems = useMemo(
		() =>
			cuentaCorrienteActivityFilteredItems.slice(0, cuentaCorrienteActivityVisibleCount),
		[cuentaCorrienteActivityFilteredItems, cuentaCorrienteActivityVisibleCount]
	);

	const cuentaCorrienteActivityGroupedItems = useMemo(() => {
		const groups = [];

		cuentaCorrienteActivityVisibleItems.forEach((item) => {
			const previousGroup = groups[groups.length - 1];

			if (!previousGroup || previousGroup.key !== item.dateKey) {
				groups.push({
					key: item.dateKey,
					label: item.dateLabel,
					items: [item],
				});
				return;
			}

			previousGroup.items.push(item);
		});

		return groups;
	}, [cuentaCorrienteActivityVisibleItems]);

	const cuentaCorrienteActivitySummary = useMemo(() => {
		const currentMonth = dayjs();
		return {
			monthLabel: formatCuentaCorrienteMonthLabel(currentMonth),
			salidas: Number(
				resumenCuentaCorrienteMesActualSeleccionado?.totalDeudaPeriodo || 0
			),
			ingresos: Number(
				resumenCuentaCorrienteMesActualSeleccionado?.totalPagosAprobadosPeriodo || 0
			),
		};
	}, [resumenCuentaCorrienteMesActualSeleccionado]);

	const cuentaCorrienteActivityHasMore =
		cuentaCorrienteActivityVisibleCount < cuentaCorrienteActivityFilteredItems.length;

	useEffect(() => {
		if (!usuario?.decodeUser?.dni || !token) return;
		console.log("[Actividad CC][frontend] render actividad", {
			filtroVista: cuentaCorrienteActivityFilter,
			visibleCount: cuentaCorrienteActivityVisibleCount,
			totalFiltrados: cuentaCorrienteActivityFilteredItems.length,
			totalVisibles: cuentaCorrienteActivityVisibleItems.length,
			idsFiltrados: cuentaCorrienteActivityFilteredItems.map((item) => ({
				id: Number(item?.id || 0) || null,
				tipo: item?.tipo || null,
				fecha: item?.movementDate || null,
			})),
			idsVisibles: cuentaCorrienteActivityVisibleItems.map((item) => ({
				id: Number(item?.id || 0) || null,
				tipo: item?.tipo || null,
				fecha: item?.movementDate || null,
			})),
		});
	}, [
		cuentaCorrienteActivityFilter,
		cuentaCorrienteActivityFilteredItems,
		cuentaCorrienteActivityVisibleCount,
		cuentaCorrienteActivityVisibleItems,
		token,
		usuario?.decodeUser?.dni,
	]);

	useEffect(() => {
		setCuentaCorrienteActivityVisibleCount(CUENTA_CORRIENTE_ACTIVITY_PAGE_SIZE);
	}, [
		cuentaCorrienteActivityFilter,
		selectedSedeCC,
		selectedSegmentoCC,
		showCuentaCorrienteActividad,
	]);

	useEffect(() => {
		if (!showCuentaCorrienteActividad) {
			setCuentaCorrienteActivityScrolled(false);
		}
	}, [showCuentaCorrienteActividad]);

	useEffect(() => {
		if (!showCuentaCorrienteActividad) return undefined;

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [showCuentaCorrienteActividad]);

	useEffect(() => {
		if (!showCuentaCorrienteActividad || !cuentaCorrienteActivityHasMore) return undefined;

		const target = cuentaCorrienteActivityLoadMoreRef.current;
		if (!target) return undefined;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) return;

				setCuentaCorrienteActivityVisibleCount((current) =>
					Math.min(
						current + CUENTA_CORRIENTE_ACTIVITY_PAGE_SIZE,
						cuentaCorrienteActivityFilteredItems.length
					)
				);
			},
			{ threshold: 0.2 }
		);

		observer.observe(target);

		return () => {
			observer.disconnect();
		};
	}, [
		showCuentaCorrienteActividad,
		cuentaCorrienteActivityHasMore,
		cuentaCorrienteActivityFilteredItems.length,
	]);

	useEffect(() => {
		voucherAcknowledgedSignatureRef.current = "";
		setVoucherAcknowledgementError("");
		setOpenVouchersActivosDialog(false);
	}, [usuario?.decodeUser?.id, token]);

	useEffect(() => {
		if (
			!canShowVouchersActivosDialog ||
			!voucherNotificationSignature ||
			voucherAcknowledgedSignatureRef.current === voucherNotificationSignature
		) {
			setOpenVouchersActivosDialog(false);
			return;
		}
		setVoucherAcknowledgementError("");
		setOpenVouchersActivosDialog(true);
	}, [canShowVouchersActivosDialog, voucherNotificationSignature]);

	const handleAcknowledgeVoucherNotifications = useCallback(async () => {
		if (!voucherNotificationSignature || vouchersConNotificacionPendiente.length === 0) {
			setOpenVouchersActivosDialog(false);
			return;
		}

		setVoucherAcknowledgementError("");
		try {
			await reconocerNotificacionesVouchers({
				variables: {
					idCliente: Number(usuario?.decodeUser?.cliente || 0) || null,
					dni: String(usuario?.decodeUser?.dni || ""),
					idUsuario: Number(usuario?.decodeUser?.id || 0) || null,
					appUsuario: usuario?.decodeUser?.sistema || null,
					notificaciones: vouchersConNotificacionPendiente.map((voucher) => ({
						idVoucher: voucher.id,
						version: voucher.versionNotificacion,
					})),
					token,
				},
			});

			voucherAcknowledgedSignatureRef.current = voucherNotificationSignature;
			setOpenVouchersActivosDialog(false);
			refetchVouchersActivosCliente().catch((refetchError) => {
				console.error("No se pudieron actualizar los vouchers reconocidos:", refetchError);
			});
		} catch (error) {
			console.error("No se pudieron reconocer las novedades de vouchers:", error);
			setVoucherAcknowledgementError(
				"No pudimos guardar la confirmación. Presioná Entendido para volver a intentar."
			);
			refetchVouchersActivosCliente().catch((refetchError) => {
				console.error("No se pudieron recargar los vouchers luego del error:", refetchError);
			});
		}
	}, [
		reconocerNotificacionesVouchers,
		refetchVouchersActivosCliente,
		token,
		usuario?.decodeUser?.cliente,
		usuario?.decodeUser?.dni,
		usuario?.decodeUser?.id,
		usuario?.decodeUser?.sistema,
		voucherNotificationSignature,
		vouchersConNotificacionPendiente,
	]);

	const [getHorariosMenu, { data: horariosMenu }] = useLazyQuery(
		gql`
      query getHorariosMenuDelDia($token: String) {
        horariosMenuDelDia(token: $token) {
          id
          turno
          horarioInicio
          horarioFin
        }
      }
    `,
		{
			fetchPolicy: "no-cache",
			cachePolicy: "no-cache",
		}
	);

	useEffect(() => {
		// El enlace de ingreso trae el token una vez; después se lee del navegador.
		const newToken = captureAppToken();
		if (newToken) {
			decodeUser({
				variables: {
					token: newToken,
				},
			});
			setToken(newToken);
			registrarSesionArchivosApp(newToken);
			getHorariosMenu({
				variables: {
					token: newToken,
				},
			});
		}
	}, []);

	useEffect(() => {
		if (usuario) {
			getPedidos({
				variables: {
					app: usuario.decodeUser.sistema,
					idUser: usuario.decodeUser.id,
					dni: usuario.decodeUser.dni,
					limit,
					token,
				},
			});
		}

		if (usuario && token) {
			getCuentaCorriente({
				variables: {
					token: token,
				},
			});
		}
	}, [usuario, limit, token]);

	useEffect(() => {
		if (
			!token ||
			!usuario?.decodeUser?.dni ||
			!idClienteActual ||
			String(responsePago?.responseCuenta?.idCliente) !== String(idClienteActual)
		) return;

		refetchCC();
		refetchCCMobile();
		refetchPagosCCListado();
		refetchCuentaCorrienteActivity();
		refetchCuentaCorrienteResumen();
		refetchVouchersActivosCliente();
		refetchVouchersClienteHistorial();
		if (expandedCC || expandedCCResponsive) {
			refetchReporteCCPeriodo();
		}
		getCuentaCorriente({
			variables: {
				token: token,
			},
		});
	}, [
		responsePago,
		idClienteActual,
		expandedCC,
		expandedCCResponsive,
		refetchCC,
		refetchCCMobile,
		refetchPagosCCListado,
		refetchCuentaCorrienteActivity,
		refetchCuentaCorrienteResumen,
		refetchVouchersActivosCliente,
		refetchVouchersClienteHistorial,
		refetchReporteCCPeriodo,
		getCuentaCorriente,
		token,
		usuario?.decodeUser?.dni,
	]);

	const handleOpenFacturaModal = (factura, empresa) => {
		setFacturaData(factura); // Guardar los datos de la factura
		setEmpresaData(empresa); // Guardar los datos de la empresa
		setOpenModalFactura(true); // Abrir el modal
	};

	const handleCloseFacturaModal = () => {
		setOpenModalFactura(false); // Cerrar el modal
		setFacturaData(null); // Limpiar los datos de la factura
		setEmpresaData(null); // Limpiar los datos de la empresa
	};

	const handlePrintFactura = (venta, empresa) => {
		handleOpenFacturaModal(venta, empresa);
	};

	const handleOpenMenu = useCallback(
		(e, item) => {
			menu({
				open: e.currentTarget,
				align: "right",
				options: [
					...(item?.tipo === "PagoCuentaCorriente"
						? [
							{
								name: "Ver comprabante de pago",
								icon: { icon: "attach_file", color: "primary" },
								disabled: !item.file,
								onClick: () => {
									if (item?.tipo === "PagoCuentaCorriente" && item.file) {
										window.open(resolveStoredFileUrl(item?.file), "_blank");
									} else {
										confirmar({
											title: "Error",
											message: "No hay comprobante de pago disponible",
											icon: "error",
										});
									}
								},
							},
						]
						: []),
					...(item?.tipo === "CuentaCorriente" && item?.idVenta
						? [
							{
								name: "Ver factura",
								icon: { icon: "receipt", color: getUproColor("uproGreen") },
								disabled: !item.idVenta || !token,
								onClick: () => {
									getVenta({ variables: { id: item.idVenta, token } }).then((res) => {
										handlePrintFactura(res.data?.venta, usuario?.decodeUser?.empresa);
									});
								},
							},
						]
						: []),
					...(item?.tipo === "PagoCuentaCorriente" && item?.motivoCancelacion
						? [
							{
								name: "Ver motivo de cancelación",
								onClick: () => {
									confirmar({
										title: "Motivo de cancelación",
										message: item.motivoCancelacion,
										icon: "cancel",
									});
								},
								icon: { icon: "block", color: "error" },
							},
						]
						: []),
				],
			});
		},
		[token, menu]
	);

	useEffect(() => {
		if (data) {
			setPedidosHoy(
				data.misPedidosPublic
					.filter(
						(pedido) =>
							dayjs(pedido.fechaPedido).format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD") &&
							pedido.estado !== "ANULADO" &&
							pedido.tipo === "Menú"
					)
					.map((pedido) => {
						return {
							id: pedido.id,
							turnoReserva: pedido.turnoReserva,
							estado: pedido.estado,
						};
					})
			);
			setRows(
				getRows(
					data.misPedidosPublic,
					(item) => {
						setSelectedPedido(item);
						setOpenModalPedidoDetalle(true);
					},
					(item) => {
						setSelectedPedido(item);
						setOpenModalValorarPedido(true);
					}
				)
			);
			setPagesInfo(data.paginationInfo);
		}
	}, [data]);

	const handleContextMenu = (e) => {
		e.preventDefault();
	};

	const handleOrderBy = (campo) => {
		if (orderBy.campo === campo) {
			if (orderBy.orden === "ASC") {
				setOrderBy({
					campo: campo,
					orden: "DESC",
				});
			} else {
				setOrderBy({
					campo: campo,
					orden: "ASC",
				});
			}
		} else {
			setOrderBy({
				campo: campo,
				orden: "ASC",
			});
		}
	};

	const [totalCCSaldo, setTotalCC] = useState(0);
	useEffect(() => {
		if (dataCC) {
			setTotalCC(dataCC?.cuentasCorrientesToken?.reduce((acc, curr) => acc + curr.total, 0));
		}
	}, [dataCC]);

	const refrescarSistemaComedorPostAlta = useCallback(() => {
		const decodeUser = usuario?.decodeUser;
		if (!decodeUser || !token) return;

		getPedidos({
			variables: {
				app: decodeUser.sistema,
				idUser: decodeUser.id,
				dni: decodeUser.dni,
				limit,
				token,
			},
		});

		getCuentaCorriente({
			variables: {
				token,
			},
		});

		refetchCC();
		refetchCCMobile();
		refetchPagosCCListado();
		refetchCuentaCorrienteActivity();
		refetchCuentaCorrienteResumen();
		refetchVouchersActivosCliente();
		refetchVouchersClienteHistorial();

		if (shouldFetchReporteCCPeriodo) {
			refetchReporteCCPeriodo();
		}
	}, [
		usuario,
		token,
		limit,
		getPedidos,
		getCuentaCorriente,
		refetchCC,
		refetchCCMobile,
		refetchPagosCCListado,
		refetchCuentaCorrienteActivity,
		refetchCuentaCorrienteResumen,
		refetchVouchersActivosCliente,
		refetchVouchersClienteHistorial,
		shouldFetchReporteCCPeriodo,
		refetchReporteCCPeriodo,
	]);

	useEffect(() => {
		if (!idClienteActual) return;
		window.addEventListener(GRAPHQL_RECONNECTED_EVENT, refrescarSistemaComedorPostAlta);
		return () => {
			window.removeEventListener(GRAPHQL_RECONNECTED_EVENT, refrescarSistemaComedorPostAlta);
		};
	}, [idClienteActual, refrescarSistemaComedorPostAlta]);

	useEffect(() => {
		const nuevoPedido = nuevoPedidoEvent?.nuevoPedido;
		const pedidoModificado = pedidoModificadoEvent?.pedidoModificado;
		const evento = pedidoModificado || nuevoPedido;
		if (!evento?.id) return;

		const tipoEvento = pedidoModificado ? "modificado" : "nuevo";
		const eventKey = `${tipoEvento}:${evento.id}:${evento.updatedAt || ""}`;
		if (ultimoPedidoSyncEventRef.current === eventKey) return;
		ultimoPedidoSyncEventRef.current = eventKey;
		refrescarSistemaComedorPostAlta();
	}, [nuevoPedidoEvent, pedidoModificadoEvent, refrescarSistemaComedorPostAlta]);

	const mediodia = horariosMenu?.horariosMenuDelDia?.find((h) => h.turno === "Mediodía");
	const noche = horariosMenu?.horariosMenuDelDia?.find((h) => h.turno === "Noche");

	useEffect(() => {
		if (idSede) {
			getPrecioMenu({
				variables: {
					idSede: idSede,
					tipoUsuario: "Particular",
				},
			});
		}
	}, [idSede]);

	const handleChangeSedeCuentaCorriente = (event) => {
		const nextSedeId = Number(event?.target?.value || 0);
		if (!nextSedeId) return;
		setSelectedSedeCC(nextSedeId);
		setIdSede(String(nextSedeId));
		localStorage.setItem("idSede", String(nextSedeId));
		localStorage.removeItem("selectedLocation");
	};

	const togglePagoCC = (idPago) => {
		setExpandedPagosCC((prev) => ({ ...prev, [idPago]: !prev[idPago] }));
	};

	const renderPagoCardCuentaCorriente = (item) => {
		const idPago = item?.id;
		const isExpanded = Boolean(expandedPagosCC[idPago]);
		const fileUrl = resolveStoredFileUrl(item?.file);
		const isPendienteTransferencia = item?.estado === "Pendiente" && isTransferenciaPago(item);
		const isRechazadoTransferencia = item?.estado === "Rechazado" && isTransferenciaPago(item);
		const backgroundColor = isPendienteTransferencia
			? "#FFF3E0"
			: isRechazadoTransferencia
				? "#FFEBEE"
				: "#FFFFFF";

		return (
			<Card key={`pago-cc-${idPago}`} sx={{ p: 2, border: "1px solid #e6e6e6", backgroundColor }}>
				<SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
					<SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
						<Icon
							color="primary"
							sx={{
								cursor: "pointer",
								transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
								transition: "all 0.2s",
							}}
							onClick={() => togglePagoCC(idPago)}
						>
							play_circle_outline
						</Icon>
						<SoftTypography variant="button" fontWeight="bold">
							#{idPago}
						</SoftTypography>
						{item?.medioPago ? (
							<SoftTypography variant="caption" fontWeight="bold" color="dark">
								{item?.medioPago}
							</SoftTypography>
						) : null}
						<SoftTypography variant="caption" color="text">
							{dayjs(item?.createdAt).format("DD/MM/YYYY HH:mm")}
						</SoftTypography>
					</SoftBox>
				</SoftBox>

				<Collapse in={isExpanded} timeout="auto" unmountOnExit>
					<Divider sx={{ my: 1.5 }} />
					<SoftBox>
						<Card
							sx={{
								border: "1px solid #e6e6e6",
								p: 1.5,
								cursor: fileUrl ? "pointer" : "default",
								mb: 1.5,
							}}
							onClick={() => {
								if (fileUrl) setPreviewFileCC(item?.file);
							}}
						>
							{fileUrl ? (
								isImageFile(item?.file) ? (
									<img
										src={fileUrl}
										alt="comprobante"
										style={{ width: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 8 }}
									/>
								) : (
									<SoftBox
										display="flex"
										flexDirection="column"
										alignItems="center"
										justifyContent="center"
										minHeight={120}
									>
										<Icon color="primary" sx={{ mb: 1 }}>
											picture_as_pdf
										</Icon>
										<SoftTypography variant="caption" color="text">
											Ver comprobante
										</SoftTypography>
									</SoftBox>
								)
							) : (
								<SoftBox
									display="flex"
									flexDirection="column"
									alignItems="center"
									justifyContent="center"
									minHeight={120}
								>
									<Icon color="secondary" sx={{ mb: 1 }}>
										image_not_supported
									</Icon>
									<SoftTypography variant="caption" color="text">
										Sin comprobante adjunto
									</SoftTypography>
								</SoftBox>
							)}
						</Card>

						<SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
							<SoftTypography variant="caption" fontWeight="bold">
								Estado: {getPagoEstadoVisible(item) || "Sin estado"}
							</SoftTypography>
						</SoftBox>

						{isRechazadoTransferencia ? (
							<SoftBox mt={1.5}>
								<SoftTypography variant="caption" fontWeight="bold">
									Motivo de rechazo
								</SoftTypography>
								<SoftTypography variant="caption" color="text" display="block" mt={0.5}>
									{item?.descripcion || "Sin motivo informado"}
								</SoftTypography>
							</SoftBox>
						) : null}
					</SoftBox>
				</Collapse>
			</Card>
		);
	};

	const renderPagosCuentaCorriente = ({ mobile = false } = {}) => {
		const pagosVisibles = mobile ? pagosCuentaCorrienteMobile : pagosCuentaCorriente;

		return (
			<SoftBox p={mobile ? 2 : 3} pt={mobile ? 0 : 1}>
				<SoftTypography variant="h6" textAlign={mobile ? "left" : "center"} mb={1}>
					Pagos en cuenta corriente
				</SoftTypography>
				<Divider sx={{ mb: 1.5 }} />
				{esFuncionario && <Grid container spacing={1.5} mb={1.5}>
					<Grid item xs={12} sm={6}>
						<Card sx={{ p: 1.5, border: "1px solid #e6e6e6", backgroundColor: "#FFF3E0" }}>
							<SoftTypography variant="caption" fontWeight="bold">
								Pagos pendientes (Transferencia)
							</SoftTypography>
							<SoftTypography variant="h6" color="warning" mt={0.5}>
								{cantidadPagosPendientes} pagos
							</SoftTypography>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Card sx={{ p: 1.5, border: "1px solid #e6e6e6", backgroundColor: "#FFEBEE" }}>
							<SoftTypography variant="caption" fontWeight="bold">
								Pagos rechazados (Transferencia)
							</SoftTypography>
							<SoftTypography variant="h6" color="error" mt={0.5}>
								{cantidadPagosRechazados} pagos
							</SoftTypography>
						</Card>
					</Grid>
				</Grid>}
				{loadingPagosCCListado ? (
					<Loading />
				) : pagosVisibles.length > 0 ? (
					<SoftBox display="flex" flexDirection="column" gap={1.5}>
						{pagosVisibles.map(renderPagoCardCuentaCorriente)}
					</SoftBox>
				) : (
					<SinDatos />
				)}

				{mobile && pagosCuentaCorriente.length > pagosVisibles.length ? (
					<SoftBox mt={1.5}>
						<SoftButton color="primary" circular fullWidth onClick={handleLoadMoreMobile}>
							Cargar más pagos
						</SoftButton>
					</SoftBox>
				) : null}
			</SoftBox>
		);
	};

	const getMetricVisual = (metricType, value) => {
		const parsedValue = Number(value || 0);
		if (metricType === "inicio") {
			return {
				icon: "history",
				iconColor: parsedValue < 0 ? "error" : parsedValue > 0 ? "success" : "info",
				valueColor: parsedValue < 0 ? "error" : parsedValue > 0 ? "success" : "text",
			};
		}
		if (metricType === "deuda") {
			return {
				icon: "attach_money",
				iconColor: parsedValue < 0 ? "error" : "info",
				valueColor: parsedValue < 0 ? "error" : "text",
			};
		}
		if (metricType === "pagos") {
			return {
				icon: "payments",
				iconColor: "success",
				valueColor: "success",
			};
		}
		if (metricType === "beca_registrada") {
			return {
				icon: "redeem",
				iconColor: "info",
				valueColor: "info",
			};
		}
		if (metricType === "beca_aplicada") {
			return {
				icon: "rule",
				iconColor: "info",
				valueColor: "info",
			};
		}
		return {
			icon: "account_balance_wallet",
			iconColor: parsedValue > 0 ? "success" : parsedValue < 0 ? "error" : "info",
			valueColor: parsedValue > 0 ? "success" : parsedValue < 0 ? "error" : "text",
		};
	};

	const renderCuentaCorrientePeriodoCards = ({ mobile = false } = {}) => {
		if (loadingReporteCCPeriodo) {
			return (
				<SoftBox p={mobile ? 2 : 3}>
					<Loading />
				</SoftBox>
			);
		}

		return (
			<SoftBox p={mobile ? 2 : 3} pt={mobile ? 1 : 2}>
				{sedesCuentaCorriente.length > 1 && (
					<SoftBox
						display="flex"
						justifyContent="flex-end"
						mb={2}
						sx={{
							flexDirection: { xs: "column", sm: "row" },
							alignItems: { xs: "stretch", sm: "center" },
							gap: 1,
						}}
					>
						<SoftTypography variant="button" fontWeight="bold">
							Sede
						</SoftTypography>
						<Select
							size="small"
							value={selectedSedeCC || ""}
							onChange={handleChangeSedeCuentaCorriente}
							sx={{
								minWidth: { xs: "100%", sm: "260px" },
							}}
						>
							{sedesCuentaCorriente.map((sede) => (
								<MenuItem key={sede.id} value={Number(sede.id)}>
									{sede.nombre}
								</MenuItem>
							))}
						</Select>
					</SoftBox>
				)}

				{rowsCCPeriodo.length === 0 ? (
					<SinDatos />
				) : (
					<SoftBox display="flex" flexDirection="column" gap={2}>
						{rowsCCPeriodoVisible.map((row, index) => {
							const metrics = [
								{ label: "Total inicio", value: row?.totalInicio, type: "inicio" },
								{ label: "Total deuda", value: row?.totalDeuda, type: "deuda" },
								{ label: "Total pagos", value: row?.totalPagos, type: "pagos" },
								{ label: "Beca registrada", value: row?.totalBecasRegistradas, type: "beca_registrada" },
								{ label: "Beca aplicada", value: row?.totalBecas, type: "beca_aplicada" },
								{ label: "Saldo acumulado", value: row?.acumulado, type: "saldo" },
							];

							return (
								<Card
									key={`${row?.periodo || "periodo"}-${index}`}
									sx={{
										border: "1px solid rgba(0,0,0,0.12)",
										borderRadius: "12px",
									}}
								>
									<SoftBox p={2}>
										<SoftBox
											display="flex"
											justifyContent="space-between"
											alignItems="center"
											mb={1.5}
											gap={1}
										>
											<SoftTypography variant="h6" fontWeight="bold">
												{row?.periodo}
											</SoftTypography>
											<SoftBadge
												color="primary"
												size="xs"
												container
												badgeContent="Resumen"
											/>
										</SoftBox>
										<Grid container spacing={1.5}>
											{metrics.map((metric) => {
												const visual = getMetricVisual(metric.type, metric.value);
												return (
													<Grid item xs={12} sm={6} md={4} lg={3} key={`${row?.periodo}-${metric.label}`}>
														<Card sx={{ p: 2, border: "1px solid #e6e6e6", height: "100%" }}>
															<SoftBox display="flex" alignItems="center" gap={1}>
																<Icon color={visual.iconColor}>{visual.icon}</Icon>
																<SoftTypography variant="caption" color="text" fontWeight="bold">
																	{metric.label}
																</SoftTypography>
															</SoftBox>
															<SoftTypography variant="h6" color={visual.valueColor} mt={0.5}>
																{valorAPesos(Number(metric.value || 0))}
															</SoftTypography>
														</Card>
													</Grid>
												);
											})}
										</Grid>
									</SoftBox>
								</Card>
							);
						})}
						<CustomPagination
							loading={loadingReporteCCPeriodo}
							length={rowsCCPeriodoVisible.length}
							total={rowsCCPeriodo.length}
							pages={pagesCCPeriodo}
							page={pageCCPeriodo}
							setPage={setPageCCPeriodo}
							noCard
						/>
					</SoftBox>
				)}
			</SoftBox>
		);
	};

	const getCuentaCorrienteActivityVisual = (item) => {
		if (item?.tipo === "pago") {
			return {
				icon: "payments",
				iconBackground: "linear-gradient(135deg, #E7F8EA 0%, #D8F4DE 100%)",
				iconColor: "#16A34A",
				amountColor: "#16A34A",
				sign: "+",
			};
		}

		return {
			icon: String(item?.subtitle || "").toLowerCase().includes("cafeter") ? "coffee" : "restaurant",
			iconBackground: "linear-gradient(135deg, #FFF0D8 0%, #FFE6BF 100%)",
			iconColor: "#F59E0B",
			amountColor: "#FF3366",
			sign: "-",
		};
	};

	const getCuentaCorrienteStatusColor = (status) => {
		if (!status) return "text";
		const normalized = String(status || "").trim().toUpperCase();
		if (normalized === "APROBADO" || normalized === "CERRADO" || normalized === "PAGADO") {
			return "success";
		}
		if (
			normalized === "PENDIENTE" ||
			normalized === "PENDIENTE DE COBRO" ||
			normalized === "RESERVADO" ||
			normalized === "PENDIENTE DE PAGO"
		) {
			return "warning";
		}
		if (
			normalized === "REVISION" ||
			normalized === "PAGO EN REVISION" ||
			normalized === "PAGO EN REVISIÓN" ||
			normalized === "ABIERTO"
		) {
			return "info";
		}
		if (normalized === "RECHAZADO" || normalized === "ANULADO") return "error";
		return "text";
	};

	const handleCuentaCorrienteActivityItemClick = useCallback(
		(item) => {
			if (item?.tipo === "pedido" && item?.pedido?.id) {
				setSelectedPedido(item.pedido);
				setOpenModalPedidoDetalle(true);
				return;
			}

			if (item?.tipo !== "pago") return;

			if (item?.file) {
				setPreviewFileCC(item.file);
				return;
			}

			if (item?.motivoCancelacion) {
				confirmar({
					title: "Motivo de cancelacion",
					message: item.motivoCancelacion,
					icon: "cancel",
				});
			}
		},
		[confirmar]
	);

	const renderCuentaCorrienteActivityRow = (item, { compact = false, grouped = false } = {}) => {
		const visual = getCuentaCorrienteActivityVisual(item);
		const estadoVisible = item?.estadoVisible || item?.estado;
		const statusColor = getCuentaCorrienteStatusColor(estadoVisible);
		const isFuncionarioPagoDetalle = esFuncionario && item?.tipo === "pago" && grouped;
		const shouldHideAmount =
			esFuncionario && item?.tipo === "pago" && String(item?.estado || "").toUpperCase() === "PENDIENTE";
		const canOpen =
			(item?.tipo === "pedido" && Boolean(item?.pedido?.id)) ||
			(item?.tipo === "pago" && Boolean(item?.file || item?.motivoCancelacion));

		return (
			<Card
				key={item.key}
				onClick={() => canOpen && handleCuentaCorrienteActivityItemClick(item)}
				onKeyDown={(event) => {
					if (!canOpen) return;
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						handleCuentaCorrienteActivityItemClick(item);
					}
				}}
				role={canOpen ? "button" : undefined}
				tabIndex={canOpen ? 0 : -1}
				sx={{
					p: compact ? 1.65 : 2,
					borderRadius: compact ? "20px" : "22px",
					border: "1px solid rgba(226, 232, 240, 0.95)",
					boxShadow: compact
						? "0 10px 26px rgba(15, 23, 42, 0.055)"
						: "0 12px 30px rgba(15, 23, 42, 0.06)",
					background: "#FFFFFF",
					cursor: canOpen ? "pointer" : "default",
					transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
					"&:hover": canOpen
						? {
							transform: "translateY(-1px)",
							boxShadow: "0 18px 40px rgba(15, 23, 42, 0.1)",
							borderColor: "rgba(8, 145, 178, 0.28)",
						}
						: undefined,
					"&:focus-visible": canOpen
						? {
							outline: "3px solid rgba(34, 211, 238, 0.35)",
							outlineOffset: "2px",
						}
						: undefined,
				}}
			>
				<SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
					<SoftBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
						<SoftBox
							sx={{
								width: compact ? 52 : 56,
								height: compact ? 52 : 56,
								minWidth: compact ? 52 : 56,
								borderRadius: compact ? "16px" : "18px",
								background: visual.iconBackground,
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Icon sx={{ color: visual.iconColor, fontSize: compact ? "1.3rem" : "1.5rem" }}>
								{visual.icon}
							</Icon>
						</SoftBox>

						<SoftBox minWidth={0}>
							<SoftBox
								display="flex"
								alignItems={{ xs: "flex-start", sm: "center" }}
								flexDirection={{ xs: "column", sm: "row" }}
								gap={{ xs: 0.25, sm: 1 }}
							>
								<SoftTypography
									variant={compact ? "button" : "h6"}
									fontWeight="bold"
									sx={{ color: "#0F172A", letterSpacing: "-0.01em" }}
								>
									#{item.id}
								</SoftTypography>
								<SoftTypography
									variant="caption"
									sx={{ color: "rgba(100, 116, 139, 0.65)", display: { xs: "none", sm: "block" } }}
								>
									|
								</SoftTypography>
								<SoftTypography
									variant={compact ? "button" : "h6"}
									fontWeight={compact ? "medium" : "bold"}
									sx={{ color: "#0F172A", letterSpacing: "-0.01em" }}
								>
									{item.label}
								</SoftTypography>
							</SoftBox>

							<SoftBox
								display="flex"
								alignItems={{ xs: "flex-start", sm: "center" }}
								flexDirection={{ xs: "column", sm: "row" }}
								gap={{ xs: 0.25, sm: 1 }}
								mt={0.25}
							>
								<SoftTypography variant="caption" sx={{ color: "#64748B" }}>
									{item.subtitle}
								</SoftTypography>
								{estadoVisible ? (
									<SoftTypography variant="caption" color={statusColor} fontWeight="bold">
										{formatCuentaCorrienteStatusLabel(estadoVisible)}
									</SoftTypography>
								) : null}
							</SoftBox>
						</SoftBox>
					</SoftBox>

					<SoftBox textAlign="right" flexShrink={0}>
						{!shouldHideAmount ? (
							<SoftTypography
								variant={compact ? "button" : "h6"}
								fontWeight="bold"
								sx={{ color: visual.amountColor }}
							>
								{`${visual.sign} $ ${formatMoneyPunto(item.amountAbs)}`}
							</SoftTypography>
						) : null}
					</SoftBox>
				</SoftBox>

				{isFuncionarioPagoDetalle ? (
					<SoftBox
						mt={1.5}
						pt={1.5}
						sx={{
							borderTop: "1px solid rgba(148, 163, 184, 0.18)",
						}}
					>
						<SoftBox
							display="flex"
							justifyContent="space-between"
							alignItems={{ xs: "flex-start", sm: "center" }}
							flexDirection={{ xs: "column", sm: "row" }}
							gap={1.5}
						>
							<SoftBox display="flex" alignItems="center" gap={1}>
								<Icon fontSize="small" sx={{ color: "#0891B2" }}>
									attach_file
								</Icon>
								<SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0F172A" }}>
									Archivo adjunto
								</SoftTypography>
								<SoftTypography variant="caption" sx={{ color: "#64748B" }}>
									{item?.file ? "Comprobante cargado" : "Sin comprobante"}
								</SoftTypography>
							</SoftBox>

							{item?.file ? (
								<SoftButton
									color="info"
									variant="outlined"
									circular
									onClick={(event) => {
										event.stopPropagation();
										setPreviewFileCC(item.file);
									}}
								>
									<Icon>visibility</Icon>
									&nbsp; Ver comprobante
								</SoftButton>
							) : null}
						</SoftBox>
					</SoftBox>
				) : null}
			</Card>
		);
	};

	const renderCuentaCorrienteActivityCard = () => {
		const isLoading = loadingCuentaCorrienteActivity || loadingCuentaCorrienteResumen;
		const saldo = saldoCuentaCorrienteUnificado;
		const saldoStatus =
			saldo < 0
				? { label: "Deuda", background: "#FEE2E2", color: "#B91C1C" }
				: saldo > 0
					? { label: "A favor", background: "#DCFCE7", color: "#15803D" }
					: { label: "Saldado", background: "#E5E7EB", color: "#475569" };

		return (
			<Card
				sx={{
					overflow: "hidden",
					borderRadius: { xs: "28px", sm: "32px" },
					border: "1px solid rgba(8, 145, 178, 0.12)",
					boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
					background: "linear-gradient(180deg, #FFFFFF 0%, #FCFEFF 100%)",
				}}
			>
				<SoftBox
					p={{ xs: 2.5, sm: 3.5 }}
					display="flex"
					justifyContent="space-between"
					alignItems={{ xs: "flex-start", md: "center" }}
					flexDirection={{ xs: "column", md: "row" }}
				>
					<SoftBox display="flex" alignItems="center" gap={2} mb={esFuncionario ? 1 : -1}>
						<Card>
							<SoftBox
								sx={{
									width: "30px",
									height: "30px",
									background: "linear-gradient(135deg, #5ED4C2 0%, #43BFB5 100%)",
									boxShadow: "0 10px 24px rgba(67, 191, 181, 0.22)",
								}}
								display="flex"
								justifyContent="center"
								alignItems="center"
							>
								<Icon fontSize="small" sx={{ color: "#fff" }}>
									account_balance_wallet
								</Icon>
							</SoftBox>
						</Card>

						<SoftBox display="flex" justifyContent="space-between" alignItems="center">
							<SoftTypography
								variant="h6"
								fontWeight="bold"
								sx={{ color: "#0F172A", lineHeight: 1.2 }}
							>
								Cuenta Corriente
							</SoftTypography>
							<SoftBox
								mt={1}
								display="flex"
								flexDirection={{ xs: "column", sm: "row" }}
								gap={1}
							>

								{mostrarSelectorSegmentoCuentaCorriente ? (
									<Select
										size="small"
										fullWidth
										value={segmentoCuentaCorrienteSeleccionado || ""}
										onChange={(event) => setSelectedSegmentoCC(String(event?.target?.value || ""))}
									>
										<MenuItem value={SEGMENTO_FUNCIONARIO}>
											{getCuentaCorrienteSegmentoLabel(SEGMENTO_FUNCIONARIO)}
										</MenuItem>
										<MenuItem value={SEGMENTO_NO_FUNCIONARIO}>
											{getCuentaCorrienteSegmentoLabel(SEGMENTO_NO_FUNCIONARIO)}
										</MenuItem>
									</Select>
								) : null}
							</SoftBox>
						</SoftBox>
					</SoftBox>

					{puedeCargarPagoCuentaCorriente ? (
						<SoftButton
							color="dark"
							circular
							onClick={() => setOpenModalCargarPagoCC(true)}
							sx={{ minWidth: { xs: "100%", md: "auto" } }}
						>
							<Icon>attach_money</Icon>
							&nbsp; Cargar pago
						</SoftButton>
					) : null}
				</SoftBox>

				<SoftBox px={{ xs: 2.5, sm: 3.5 }} pb={{ xs: 2.5, sm: 3.5 }} display="flex" flexDirection="column" gap={2}>
					<SoftBox
						sx={{
							position: "relative",
							overflow: "hidden",
							borderRadius: "24px",
							border: "1px solid rgba(226, 232, 240, 0.95)",
							boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.9)",
							px: { xs: 2.35, sm: 2.75 },
							py: { xs: 2.2, sm: 2.55 },
						}}
					>
						<SoftBox
							sx={{
								position: "absolute",
								top: -24,
								right: -18,
								width: 108,
								height: 108,
								borderRadius: "50%",
								pointerEvents: "none",
							}}
						/>

						<SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
							<SoftBox>
								<SoftBox
									component="span"
									sx={{
										display: "inline-flex",
										alignItems: "center",
										px: 1.1,
										py: 0.45,
										borderRadius: "999px",
										background: saldoStatus.background,
									}}
								>
									<SoftTypography
										variant="caption"
										fontWeight="bold"
										sx={{
											color: saldoStatus.color,
											textTransform: "uppercase",
											letterSpacing: "0.1em",
											lineHeight: 1,
										}}
									>
										{saldoStatus.label}
									</SoftTypography>
								</SoftBox>

								<SoftTypography
									variant="h3"
									fontWeight="bold"
									sx={{
										color: "#0F172A",
										lineHeight: 1,
										mt: 1.15,
										letterSpacing: "-0.03em"
									}}
								>
									{`$ ${formatMoneyPunto(Math.abs(saldo))}`}
								</SoftTypography>
							</SoftBox>
						</SoftBox>
					</SoftBox>

					<SoftBox
						sx={{
							borderRadius: "20px",
							border: `1px solid ${alertaCuentaCorrienteCard.border}`,
							background: alertaCuentaCorrienteCard.background,
							boxShadow: "0 12px 26px rgba(15, 23, 42, 0.05)",
							px: 1.8,
							py: 1.6,
						}}
					>
						<SoftBox display="flex" alignItems="center" gap={1.15}>
							<SoftBox
								sx={{
									width: 34,
									height: 34,
									minWidth: 34,
									borderRadius: "50%",
									background: alertaCuentaCorrienteCard.iconBg,
									color: alertaCuentaCorrienteCard.accent,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<Icon sx={{ fontSize: "1rem" }}>{alertaCuentaCorrienteCard.icon}</Icon>
							</SoftBox>
							<SoftBox>
								<SoftTypography
									variant="caption"
									fontWeight="bold"
									sx={{
										color: alertaCuentaCorrienteCard.accent,
										letterSpacing: "0.05em",
										textTransform: "uppercase",
									}}
								>
									Limite de cuenta corriente
								</SoftTypography>
								<SoftTypography
									variant="button"
									fontWeight="bold"
									sx={{
										color: alertaCuentaCorrienteCard.accent,
										display: "block",
										mt: 0.35,
									}}
								>
									{mensajeAlertaCuentaCorrienteCard}
								</SoftTypography>
							</SoftBox>
						</SoftBox>
					</SoftBox>

					<SoftBox display="flex" alignItems="center" gap={1.1}>
						<SoftBox
							sx={{
								width: 26,
								height: 26,
								minWidth: 26,
								borderRadius: "50%",
								border: "1.5px solid rgba(8, 145, 178, 0.28)",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								background: "rgba(236, 254, 255, 0.9)",
							}}
						>
							<Icon sx={{ fontSize: "0.95rem", color: "#0891B2" }}>schedule</Icon>
						</SoftBox>
						<SoftTypography
							variant="h6"
							fontWeight="bold"
							sx={{ color: "#0F172A", lineHeight: 1.2, letterSpacing: "-0.01em" }}
						>
							últimas actividades
						</SoftTypography>
					</SoftBox>

					{isLoading ? (
						<Loading />
					) : cuentaCorrienteActivityPreviewItems.length > 0 ? (
						<SoftBox display="flex" flexDirection="column" gap={1.6}>
							{cuentaCorrienteActivityPreviewItems.map((item) =>
								renderCuentaCorrienteActivityRow(item, { compact: true })
							)}
						</SoftBox>
					) : (
						<SinDatos texto="Sin movimientos en cuenta corriente" />
					)}

					<Divider sx={{ my: 0.5 }} />

					<SoftBox
						display="flex"
						justifyContent="flex-end"
						alignItems="center"
					>
						{cuentaCorrienteActivityItems.length > 0 ? (
							<SoftButton
								variant="text"
								color="info"
								circular
								onClick={() => setShowCuentaCorrienteActividad(true)}
								sx={{
									px: 0,
									minWidth: "auto",
									color: "#0891B2",
									"& .material-icons-round": { fontSize: "1.25rem" },
								}}
							>
								Ver todas&nbsp;
								<Icon>arrow_forward</Icon>
							</SoftButton>
						) : null}
					</SoftBox>
				</SoftBox>
			</Card>
		);
	};


	const renderVouchersBeneficiosCard = () => {
		const principal = vouchersDisponiblesCliente[0];
		const formatVoucherValue = (value, tipo) =>
			tipo === "MONTO"
				? `$ ${formatMoneyPunto(Number(value || 0))}`
				: `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })} unidades`;
		const cantidadVouchers = vouchersVisiblesCliente.length;
		const voucherUnico = cantidadVouchers === 1 ? vouchersVisiblesCliente[0] : null;
		const puedeDesplegarVoucherUnico = Boolean(voucherUnico) && !loadingVouchersClienteHistorial;
		const estadoVoucherUnico = voucherUnico?.estadoActual === "VIGENTE" ? "Disponible" : voucherUnico?.estadoActual === "VENCIDO" ? "Vencido" : "No disponible";
		const metric = (label, value, icon, onClick = null) => (
			<SoftBox
				component={onClick ? "button" : "div"}
				type={onClick ? "button" : undefined}
				onClick={onClick || undefined}
				aria-label={onClick ? label : undefined}
				sx={{
					border: "1px solid rgba(226,232,240,.95)",
					borderRadius: "14px",
					px: 1.1,
					py: 1,
					display: "flex",
					alignItems: "center",
					gap: 0.9,
					...(onClick ? { width: "100%", cursor: "pointer", background: "transparent", textAlign: "left", font: "inherit", "&:focus-visible": { outline: "3px solid #F0067D", outlineOffset: 2 } } : {}),
				}}
			>
				<SoftBox sx={{ width: 30, height: 30, minWidth: 30, borderRadius: "50%", background: "#F1F5F9", display: "grid", placeItems: "center" }}><Icon sx={{ fontSize: "0.95rem", color: "#475569" }}>{icon}</Icon></SoftBox>
				<SoftBox><SoftTypography variant="caption" color="secondary" display="block" sx={{ lineHeight: 1 }}>{label}</SoftTypography><SoftTypography variant="button" fontWeight="bold" sx={{ color: "#172554" }}>{value}</SoftTypography></SoftBox>
			</SoftBox>
		);

		if (puedeDesplegarVoucherUnico) {
			return (
				<Card sx={{ border: "1px solid rgba(240, 6, 125, 0.16)", overflow: "hidden" }}>
					<SoftBox
						component="button"
						type="button"
						onClick={() => setVoucherUnicoDesplegado((prev) => !prev)}
						aria-expanded={voucherUnicoDesplegado}
						sx={{
							width: "100%",
							border: 0,
							p: { xs: 2, sm: 2.4 },
							background: "transparent",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 2,
							textAlign: "left",
							cursor: "pointer",
							"&:hover": { background: "rgba(240, 6, 125, 0.025)" },
							"&:focus-visible": { outline: "2px solid #F0067D", outlineOffset: "-2px" },
						}}
					>
						<SoftBox display="flex" alignItems="center" gap={1.35} minWidth={0}>
							<SoftBox
								sx={{
									width: 44,
									height: 44,
									minWidth: 44,
									borderRadius: "14px",
									display: "grid",
									placeItems: "center",
									background: "linear-gradient(135deg, #FFE7F3, #FFF4F9)",
								}}
							>
								<Icon sx={{ color: "#F0067D" }}>redeem</Icon>
							</SoftBox>

							<SoftBox minWidth={0}>
								<SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#172554", lineHeight: 1.08 }}>
									Vouchers y beneficios
								</SoftTypography>
								<SoftTypography
									variant="button"
									color="secondary"
									sx={{ display: "block", mt: 0.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
								>
									{/* CORREGIDO: El texto estático ahora está envuelto correctamente junto a las variables */}
									{voucherUnico.nombreVoucher}
								</SoftTypography>
							</SoftBox>
						</SoftBox>

						<Icon sx={{ color: "#F0067D" }}>
							{voucherUnicoDesplegado ? "expand_less" : "expand_more"}
						</Icon>
					</SoftBox>

					<Collapse in={voucherUnicoDesplegado} timeout="auto" unmountOnExit>
						<SoftBox px={{ xs: 2, sm: 2.4 }} pb={{ xs: 2, sm: 2.4 }}>
							<Divider sx={{ mb: 1.6, borderColor: "rgba(240, 6, 125, 0.12)" }} />
							<Grid container spacing={1}>
								<Grid item xs={12} sm={6}>
									{metric("Disponible", formatVoucherValue(voucherUnico.saldo, voucherUnico.tipo), "account_balance_wallet")}
								</Grid>
								<Grid item xs={12} sm={6}>
									{metric("Limite diario", formatVoucherValue(voucherUnico.limiteDiario, voucherUnico.tipo), "event_available")}
								</Grid>
								<Grid item xs={12} sm={6}>
									{metric("Consumido hoy", formatVoucherValue(voucherUnico.consumidoHoy, voucherUnico.tipo), "today", () => setVoucherConsumoSeleccionado({ voucher: voucherUnico, periodo: "hoy" }))}
								</Grid>
								<Grid item xs={12} sm={6}>
									{metric("Consumido acumulado", formatVoucherValue(voucherUnico.consumido, voucherUnico.tipo), "history", () => setVoucherConsumoSeleccionado({ voucher: voucherUnico, periodo: "acumulado" }))}
								</Grid>
							</Grid>

							<SoftTypography variant="caption" color="secondary" sx={{ display: "block", mt: 1.25 }}>
								Vigencia: {voucherUnico.desde ? dayjs(voucherUnico.desde).format("DD/MM/YYYY") : "-"} al{" "}
								{voucherUnico.hasta ? dayjs(voucherUnico.hasta).format("DD/MM/YYYY") : "-"}
							</SoftTypography>
							<SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mt: 0.5, color: "#F0067D" }}>
								{voucherUnico?.grupo?.sede?.nombre ? `Canjeable en ${voucherUnico.grupo.sede.nombre}` : "Canjeable en todas las sedes"}
							</SoftTypography>
							{getVoucherTargetLabel(voucherUnico) ? <SoftTypography variant="caption" color="secondary" display="block" mt={0.5}>{getVoucherTypeLabel(voucherUnico.tipo)}: {getVoucherTargetLabel(voucherUnico)}</SoftTypography> : null}
						</SoftBox>
					</Collapse>
				</Card>
			);
		}

		if (!loadingVouchersClienteHistorial && cantidadVouchers > 1) {
			return (
				<Card sx={{ border: "1px solid rgba(240, 6, 125, 0.16)", overflow: "hidden" }}><SoftBox p={{ xs: 2, sm: 2.4 }} display="flex" alignItems="center" justifyContent="space-between" gap={2}><SoftBox display="flex" alignItems="center" gap={1.35} minWidth={0}><SoftBox sx={{ width: 44, height: 44, minWidth: 44, borderRadius: "14px", display: "grid", placeItems: "center", background: "linear-gradient(135deg, #FFE7F3, #FFF4F9)" }}><Icon sx={{ color: "#F0067D" }}>redeem</Icon></SoftBox><SoftBox minWidth={0}><SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#172554", lineHeight: 1.08 }}>Vouchers y beneficios</SoftTypography><SoftTypography variant="button" color="secondary" sx={{ display: "block", mt: 0.3 }}>Disponibles ({vouchersDisponiblesCliente.length})</SoftTypography></SoftBox></SoftBox><SoftButton variant="text" color="primary" circular onClick={() => setShowVouchersDetalles(true)} sx={{ minWidth: "fit-content", px: 0, color: "#F0067D" }}>Ver todos&nbsp;<Icon>arrow_forward</Icon></SoftButton></SoftBox></Card>
			);
		}

		return (
			<Card sx={{ border: "1px solid rgba(240, 6, 125, 0.16)", overflow: "hidden" }}>
				<SoftBox p={{ xs: 2, sm: 2.4 }} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
					<SoftBox display="flex" alignItems="center" gap={1.35} minWidth={0}>
						<SoftBox sx={{ width: 44, height: 44, minWidth: 44, borderRadius: "14px", display: "grid", placeItems: "center", background: "linear-gradient(135deg, #FFE7F3, #FFF4F9)" }}>
							<Icon sx={{ color: "#F0067D" }}>redeem</Icon>
						</SoftBox>
						<SoftBox minWidth={0}>
							<SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#172554", lineHeight: 1.08 }}>Vouchers y beneficios</SoftTypography>
							{loadingVouchersClienteHistorial ? (
								<SoftTypography variant="caption" color="secondary">Cargando beneficios...</SoftTypography>
							) : principal ? (
								<SoftTypography variant="button" color="secondary" sx={{ display: "block", mt: 0.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{principal.nombreVoucher} Disponible: {formatVoucherValue(principal.saldo, principal.tipo)}
								</SoftTypography>
							) : vouchersNoDisponiblesCliente.length || vouchersHistorialCliente.length ? (
								<SoftTypography variant="button" color="secondary" sx={{ display: "block", mt: 0.3 }}>No tenes beneficios disponibles</SoftTypography>
							) : (
								<SoftTypography variant="button" color="secondary" sx={{ display: "block", mt: 0.3 }}>Todavia no tenes vouchers asignados</SoftTypography>
							)}
						</SoftBox>
					</SoftBox>
					<SoftButton variant="text" color="primary" circular onClick={() => setShowVouchersDetalles(true)} sx={{ minWidth: "fit-content", px: 0, color: "#F0067D" }}>
						Ver todos&nbsp;<Icon>arrow_forward</Icon>
					</SoftButton>
				</SoftBox>
			</Card>
		);
	};

	const renderVouchersDetallesOverlay = () => {
		if (!showVouchersDetalles) return null;

		const formatVoucherValue = (value, tipo) =>
			tipo === "MONTO"
				? `$ ${formatMoneyPunto(Number(value || 0))}`
				: `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })} unidades`;
		const getStatus = (estado) => ({
			VENCIDO: { label: "Vencido", color: "#64748B", background: "#F1F5F9" },
			SIN_SALDO: { label: "Sin saldo", color: "#B45309", background: "#FFF7ED" },
			PROXIMO: { label: "Proximamente", color: "#1D4ED8", background: "#EFF6FF" },
			NO_DISPONIBLE: { label: "No disponible", color: "#64748B", background: "#F1F5F9" },
			VIGENTE: { label: "Disponible", color: "#15803D", background: "#DCFCE7" },
		}[estado] || { label: "No disponible", color: "#64748B", background: "#F1F5F9" });
		const renderVoucher = (voucher) => {
			const status = getStatus(voucher.estadoActual);
			const metric = (label, value, icon, onClick = null) => (
				<SoftBox
					component={onClick ? "button" : "div"}
					type={onClick ? "button" : undefined}
					onClick={onClick || undefined}
					aria-label={onClick ? label : undefined}
					sx={{
						border: "1px solid rgba(226,232,240,.95)",
						borderRadius: "16px",
						px: 1.25,
						py: 1.1,
						display: "flex",
						alignItems: "center",
						gap: 1,
						...(onClick ? { width: "100%", cursor: "pointer", background: "transparent", textAlign: "left", font: "inherit", "&:focus-visible": { outline: "3px solid #F0067D", outlineOffset: 2 } } : {}),
					}}
				>
					<SoftBox sx={{ width: 32, height: 32, borderRadius: "50%", background: "#F1F5F9", display: "grid", placeItems: "center" }}><Icon sx={{ fontSize: "1rem", color: "#475569" }}>{icon}</Icon></SoftBox>
					<SoftBox><SoftTypography variant="caption" color="secondary" display="block" sx={{ lineHeight: 1 }}>{label}</SoftTypography><SoftTypography variant="button" fontWeight="bold" sx={{ color: "#172554" }}>{value}</SoftTypography></SoftBox>
				</SoftBox>
			);

			return (
				<Card key={voucher.id} sx={{ p: { xs: 1.6, sm: 2 }, borderRadius: "20px", border: "1px solid rgba(226,232,240,.95)", boxShadow: "0 12px 28px rgba(15,23,42,.05)" }}>
					<SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.2} mb={1.5}>
						<SoftBox display="flex" alignItems="center" gap={1.1} minWidth={0}>
							<SoftBox sx={{ width: 40, height: 40, minWidth: 40, borderRadius: "13px", display: "grid", placeItems: "center", background: voucher.tipo === "MONTO" ? "#EAF3FF" : "#ECFDF3" }}><Icon sx={{ color: voucher.tipo === "MONTO" ? "#2563EB" : "#16A34A" }}>{getVoucherTypeIcon(voucher.tipo)}</Icon></SoftBox>
							<SoftBox minWidth={0}><SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#172554", lineHeight: 1.1 }}>{voucher.nombreVoucher || "Voucher"}</SoftTypography><SoftTypography variant="caption" color="secondary">{getVoucherTypeLabel(voucher.tipo)}{getVoucherTargetLabel(voucher) ? ` - ${getVoucherTargetLabel(voucher)}` : ""}</SoftTypography></SoftBox>
						</SoftBox>
						<SoftBox sx={{ px: 1, py: 0.45, borderRadius: "999px", background: status.background, color: status.color, fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap" }}>{status.label}</SoftBox>
					</SoftBox>
					<Grid container spacing={1}>
						<Grid item xs={12} sm={6}>{metric("Disponible", formatVoucherValue(voucher.saldo, voucher.tipo), "account_balance_wallet")}</Grid>
						<Grid item xs={12} sm={6}>{metric("Limite diario", formatVoucherValue(voucher.limiteDiario, voucher.tipo), "event_available")}</Grid>
						<Grid item xs={12} sm={6}>{metric("Consumido hoy", formatVoucherValue(voucher.consumidoHoy, voucher.tipo), "today", () => setVoucherConsumoSeleccionado({ voucher, periodo: "hoy" }))}</Grid>
						<Grid item xs={12} sm={6}>{metric("Consumido acumulado", formatVoucherValue(voucher.consumido, voucher.tipo), "history", () => setVoucherConsumoSeleccionado({ voucher, periodo: "acumulado" }))}</Grid>
					</Grid>
					{voucher.estadoActual !== "VIGENTE" ? <SoftTypography variant="caption" color="secondary" sx={{ display: "block", mt: 1.25 }}>Vigencia: {voucher.desde ? dayjs(voucher.desde).format("DD/MM/YYYY") : "-"} al {voucher.hasta ? dayjs(voucher.hasta).format("DD/MM/YYYY") : "-"}</SoftTypography> : null}
					<SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mt: 0.7, color: "#F0067D" }}>{voucher?.grupo?.sede?.nombre ? `Canjeable en ${voucher.grupo.sede.nombre}` : "Canjeable en todas las sedes"}</SoftTypography>
				</Card>
			);
		};

		return (
			<SoftBox sx={{ position: "fixed", inset: 0, zIndex: 1400, overflowY: "auto", background: "linear-gradient(180deg, #FFF9FC 0%, #F8FAFC 100%)", p: { xs: 2, sm: 3 } }}>
				<SoftBox maxWidth="880px" mx="auto" display="flex" flexDirection="column" gap={2.25}>
					<SoftBox position="sticky" top={0} zIndex={2} py={1} sx={{ background: "rgba(255,249,252,.88)", backdropFilter: "blur(12px)" }}><SoftButton color="white" circular onClick={() => setShowVouchersDetalles(false)} sx={{ color: "#F0067D", boxShadow: "0 10px 24px rgba(15,23,42,.08)" }}><Icon>arrow_back</Icon>&nbsp; Volver</SoftButton></SoftBox>
					<SoftBox><SoftTypography variant="h2" fontWeight="bold" sx={{ color: "#172554", fontSize: { xs: "2.15rem", sm: "2.8rem" } }}>Vouchers y beneficios</SoftTypography><SoftTypography variant="button" color="secondary">Consulte el saldo y consumo de todos tus beneficios.</SoftTypography></SoftBox>
					{loadingVouchersClienteHistorial ? <Loading /> : (
						<>
							<SoftBox><SoftTypography variant="h5" fontWeight="bold" sx={{ color: "#172554", mb: 1.2 }}>Disponibles ahora ({vouchersDisponiblesCliente.length})</SoftTypography>{vouchersDisponiblesCliente.length ? <SoftBox display="flex" flexDirection="column" gap={1.25}>{vouchersDisponiblesCliente.map(renderVoucher)}</SoftBox> : <Card sx={{ p: 2, border: "1px solid rgba(226,232,240,.95)" }}><SoftTypography variant="button" color="secondary">No tenes beneficios disponibles en este momento.</SoftTypography></Card>}</SoftBox>
							{vouchersNoDisponiblesCliente.length ? <SoftBox><SoftTypography variant="h5" fontWeight="bold" sx={{ color: "#172554", mb: 1.2 }}>No disponibles ({vouchersNoDisponiblesCliente.length})</SoftTypography><SoftBox display="flex" flexDirection="column" gap={1.25}>{vouchersNoDisponiblesCliente.map(renderVoucher)}</SoftBox></SoftBox> : null}
							{vouchersHistorialCliente.length ? <SoftBox><SoftTypography variant="h5" fontWeight="bold" sx={{ color: "#172554", mb: 1.2 }}>Historial ({vouchersHistorialCliente.length})</SoftTypography><SoftBox display="flex" flexDirection="column" gap={1.25}>{vouchersHistorialCliente.map(renderVoucher)}</SoftBox></SoftBox> : null}
						</>
					)}
				</SoftBox>
			</SoftBox>
		);
	};
	const renderVoucherConsumosOverlay = () => {
		if (!voucherConsumoSeleccionado?.voucher) return null;

		const { voucher, periodo } = voucherConsumoSeleccionado;
		const consumos = (dataVoucherConsumosCliente?.misPedidosPublic || [])
			.filter(
				(pedido) =>
					Number(pedido?.venta?.idVoucher || 0) === Number(voucher.id) &&
					pedido?.estado !== "ANULADO"
			)
			.filter(
				(pedido) =>
					periodo !== "hoy" ||
					dayjs(pedido?.fechaPedido).isSame(dayjs(), "day")
			)
			.map((pedido) => ({
				id: pedido?.venta?.id || pedido.id,
				cantidad: pedido?.venta?.cantidad || 0,
				montoCubierto: pedido?.venta?.descuento || 0,
				remanente: pedido?.venta?.total || 0,
				pedido,
			}));
		const titulo = periodo === "hoy" ? "Consumido hoy" : "Consumo acumulado";
		const descripcion =
			periodo === "hoy"
				? "Pedidos realizados hoy con este voucher."
				: "Todos los pedidos no anulados realizados con este voucher.";
		const formatValor = (valor) =>
			voucher.tipo === "MONTO"
				? "$ " + formatMoneyPunto(Number(valor || 0))
				: Number(valor || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 }) + " unidad/es";
		const grupos = consumos.reduce((acc, consumo) => {
			const fecha = consumo?.pedido?.fechaPedido || consumo?.createdAt;
			const key = dayjs(fecha).isValid() ? dayjs(fecha).format("YYYY-MM-DD") : "sin-fecha";
			if (!acc[key]) acc[key] = { fecha, items: [] };
			acc[key].items.push(consumo);
			return acc;
		}, {});
		const gruposOrdenados = Object.values(grupos);

		return (
			<SoftBox sx={{ position: "fixed", inset: 0, zIndex: 1500, overflowY: "auto", background: "linear-gradient(180deg, #FFF9FC 0%, #F8FAFC 100%)", p: { xs: 2, sm: 3 } }}>
				<SoftBox maxWidth="560px" mx="auto" display="flex" flexDirection="column" gap={2.25}>
					<SoftBox position="sticky" top={0} zIndex={2} py={1} sx={{ background: "rgba(255,249,252,.88)", backdropFilter: "blur(12px)" }}>
						<SoftButton color="white" circular onClick={() => setVoucherConsumoSeleccionado(null)} sx={{ color: "#F0067D", boxShadow: "0 10px 24px rgba(15,23,42,.08)" }}><Icon>arrow_back</Icon>&nbsp; Volver</SoftButton>
					</SoftBox>
					<SoftBox>
						<SoftTypography variant="h2" fontWeight="bold" sx={{ color: "#172554", fontSize: { xs: "2.15rem", sm: "2.8rem" } }}>{titulo}</SoftTypography>
						<SoftTypography variant="button" color="secondary" display="block" mt={0.5}>{voucher.nombreVoucher || voucher?.grupo?.nombre || "Voucher"} - {descripcion}</SoftTypography>
					</SoftBox>
					{loadingVoucherConsumosCliente ? <Loading /> : errorVoucherConsumosCliente ? (
						<Card sx={{ p: 2, border: "1px solid rgba(226,232,240,.95)" }}><SoftTypography variant="button" color="error">No se pudo cargar el historial del voucher.</SoftTypography></Card>
					) : gruposOrdenados.length === 0 ? (
						<Card sx={{ p: 2, border: "1px solid rgba(226,232,240,.95)" }}><SoftTypography variant="button" color="secondary">No hay pedidos para este periodo.</SoftTypography></Card>
					) : gruposOrdenados.map((grupo) => (
						<SoftBox key={dayjs(grupo.fecha).isValid() ? dayjs(grupo.fecha).format("YYYY-MM-DD") : "sin-fecha"}>
							<SoftTypography variant="h5" fontWeight="bold" sx={{ color: "#172554", mb: 1.2 }}>{formatCuentaCorrienteDayLabel(grupo.fecha)}</SoftTypography>
							<SoftBox display="flex" flexDirection="column" gap={1.15}>
								{grupo.items.map((consumo) => (
									<Card key={consumo.id} sx={{ p: 1.6, borderRadius: "20px", border: "1px solid rgba(226,232,240,.95)", boxShadow: "0 12px 28px rgba(15,23,42,.05)" }}>
										<SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={1.25}>
											<SoftBox display="flex" alignItems="center" gap={1.15}>
												<SoftBox sx={{ width: 50, height: 50, minWidth: 50, borderRadius: "16px", display: "grid", placeItems: "center", background: "#FFF0D6" }}><Icon sx={{ color: "#F59E0B" }}>restaurant</Icon></SoftBox>
												<SoftBox><SoftTypography variant="button" fontWeight="bold" sx={{ color: "#172554" }}>#{consumo?.pedido?.id || consumo?.idPedido}</SoftTypography><SoftTypography variant="button" fontWeight="bold" display="block" sx={{ color: "#172554" }}>Pedido</SoftTypography><SoftTypography variant="caption" color="secondary" display="block">Pedido realizado</SoftTypography><SoftTypography variant="caption" fontWeight="bold" display="block" sx={{ color: consumo?.pedido?.estado === "CERRADO" ? "#65C90F" : "#475569" }}>{makeNiceText(consumo?.pedido?.estado || "")}</SoftTypography></SoftBox>
											</SoftBox>
											<SoftBox textAlign="right">
												<SoftTypography variant="button" fontWeight="bold" sx={{ color: "#F43F5E", whiteSpace: "nowrap" }}>{formatValor(consumo.cantidad)}</SoftTypography>
												<SoftTypography variant="caption" display="block">Pedido: $ {formatMoneyPunto(consumo?.pedido?.total || 0)}</SoftTypography>
												<SoftTypography variant="caption" display="block">Cubierto: $ {formatMoneyPunto(consumo.montoCubierto)}</SoftTypography>
												<SoftTypography variant="caption" display="block">Remanente: $ {formatMoneyPunto(consumo.remanente)}</SoftTypography>
											</SoftBox>
										</SoftBox>
									</Card>
								))}
							</SoftBox>
						</SoftBox>
					))}
				</SoftBox>
			</SoftBox>
		);
	};

	const renderCuentaCorrienteActivityOverlay = () => {
		if (!showCuentaCorrienteActividad) return null;

		const isLoading = loadingCuentaCorrienteActivity || loadingCuentaCorrienteResumen;
		const saldoResumenValue = formatMoneyPunto(Math.abs(saldoCuentaCorrienteUnificado));
		const pedidosResumenValue = formatMoneyPunto(cuentaCorrienteActivitySummary.salidas);
		const pagosResumenValue = formatMoneyPunto(cuentaCorrienteActivitySummary.ingresos);
		const saldoResumenColor =
			saldoCuentaCorrienteUnificado < 0
				? "#F43F5E"
				: saldoCuentaCorrienteUnificado > 0
					? "#16A34A"
					: "#0891B2";
		const saldoResumenTexto =
			saldoCuentaCorrienteUnificado < 0
				? "Pendiente de pago"
				: saldoCuentaCorrienteUnificado > 0
					? "Saldo a favor"
					: "Cuenta saldada";
		const saldoResumenFontSize =
			saldoResumenValue.length >= 7
				? { xs: "2.45rem", sm: "3.2rem" }
				: { xs: "3rem", sm: "4rem" };
		const metricCardFontSize = (value) =>
			value.length >= 7
				? { xs: "1.42rem", sm: "1.55rem" }
				: { xs: "1.75rem", sm: "2rem" };
		const filters = [
			{ id: "todos", label: "Todos" },
			{ id: "pedidos", label: "Pedidos" },
			{ id: "pagos", label: "Pagos" },
		];

		return (
			<SoftBox
				ref={cuentaCorrienteActivityScrollRef}
				onScroll={(event) => {
					setCuentaCorrienteActivityScrolled(event.currentTarget.scrollTop > 140);
				}}
				sx={{
					position: "fixed",
					inset: 0,
					zIndex: 1400,
					overflowY: "auto",
					background:
						"radial-gradient(circle at top left, rgba(34, 211, 238, 0.12), transparent 32%), linear-gradient(180deg, #F8FCFE 0%, #EFF7FA 100%)",
					p: { xs: 2, sm: 3 },
				}}
			>
				<SoftBox maxWidth="980px" mx="auto" display="flex" flexDirection="column" gap={2.5}>
					<SoftBox
						position="sticky"
						top={0}
						zIndex={10}
						display="flex"
						justifyContent="flex-start"
						alignItems="center"
						py={1}
						sx={{
							backdropFilter: "blur(14px)",
							background: "rgba(248, 252, 254, 0.86)",
						}}
					>
						<SoftButton
							color="white"
							circular
							onClick={() => {
								if (cuentaCorrienteActivityScrolled) {
									cuentaCorrienteActivityScrollRef.current?.scrollTo({
										top: 0,
										behavior: "smooth",
									});
									return;
								}

								setShowCuentaCorrienteActividad(false);
							}}
							sx={{
								boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
								color: "#0891B2",
							}}
						>
							<Icon>{cuentaCorrienteActivityScrolled ? "keyboard_arrow_up" : "arrow_back"}</Icon>
							&nbsp; {cuentaCorrienteActivityScrolled ? "Volver arriba" : "Volver"}
						</SoftButton>
					</SoftBox>

					<SoftTypography
						variant="h1"
						fontWeight="bold"
						sx={{ color: "#0F172A", fontSize: { xs: "2.6rem", sm: "3.3rem" } }}
					>
						Actividad
					</SoftTypography>

					<Card
						sx={{
							position: "relative",
							overflow: "hidden",
							p: { xs: 2, sm: 2.65 },
							borderRadius: "28px",
							border: "1px solid rgba(251, 113, 133, 0.22)",
							background:
								"radial-gradient(circle at 85% 30%, rgba(251, 113, 133, 0.12), transparent 22%), linear-gradient(180deg, #FFFDFE 0%, #FFF8FA 100%)",
							boxShadow: "0 24px 56px rgba(244, 63, 94, 0.08)",
						}}
					>
						<SoftBox
							sx={{
								position: "absolute",
								right: { xs: -22, sm: -8 },
								bottom: { xs: 70, sm: 76 },
								width: { xs: 116, sm: 170 },
								height: { xs: 116, sm: 170 },
								borderRadius: "50%",
								border: "16px solid rgba(251, 113, 133, 0.07)",
								pointerEvents: "none",
							}}
						/>
						<SoftBox
							sx={{
								position: "absolute",
								right: { xs: 6, sm: 22 },
								bottom: { xs: -16, sm: -10 },
								width: { xs: 82, sm: 112 },
								height: { xs: 98, sm: 132 },
								borderRadius: "42px 42px 14px 14px",
								background: "linear-gradient(180deg, rgba(251, 113, 133, 0.08) 0%, rgba(251, 113, 133, 0.14) 100%)",
								pointerEvents: "none",
							}}
						/>

						<SoftBox position="relative" zIndex={1}>
							<SoftBox display="flex" alignItems="center" gap={1.05}>
								<SoftBox
									sx={{
										width: 42,
										height: 42,
										minWidth: 42,
										borderRadius: "14px",
										background: "linear-gradient(135deg, rgba(251, 113, 133, 0.12) 0%, rgba(251, 113, 133, 0.2) 100%)",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<Icon sx={{ color: "#F43F5E", fontSize: "1.15rem" }}>account_balance_wallet</Icon>
								</SoftBox>
								<SoftTypography
									variant="h6"
									fontWeight="bold"
									sx={{ color: "#1E2A5A", letterSpacing: "-0.02em" }}
								>
									Saldo actual
								</SoftTypography>
							</SoftBox>

							<SoftTypography
								variant="h1"
								fontWeight="bold"
								sx={{
									color: saldoResumenColor,
									mt: { xs: 1.8, sm: 2.5 },
									mb: 0.4,
									fontSize: saldoResumenFontSize,
									lineHeight: 0.94,
									letterSpacing: "-0.05em",
									whiteSpace: "nowrap",
									fontVariantNumeric: "tabular-nums",
									textShadow:
										saldoCuentaCorrienteUnificado < 0
											? "0 12px 28px rgba(244, 63, 94, 0.14)"
											: saldoCuentaCorrienteUnificado > 0
												? "0 12px 28px rgba(22, 163, 74, 0.14)"
												: "0 12px 28px rgba(8, 145, 178, 0.14)",
								}}
							>
								{`$\u00A0${saldoResumenValue}`}
							</SoftTypography>

							<SoftTypography
								variant="button"
								fontWeight="bold"
								sx={{ color: "#7185B0", mb: { xs: 2, sm: 2.6 }, fontSize: { xs: "1rem", sm: "1.12rem" } }}
							>
								{saldoResumenTexto}
							</SoftTypography>

							<Divider sx={{ borderColor: "rgba(251, 113, 133, 0.12)", mb: { xs: 1.8, sm: 2.2 } }} />

							<Grid container spacing={1.2}>
								<Grid item xs={6}>
									<Card
										sx={{
											p: { xs: 1.3, sm: 1.55 },
											borderRadius: "18px",
											border: "1px solid rgba(226, 232, 240, 0.95)",
											background: "rgba(255, 255, 255, 0.82)",
											boxShadow: "0 10px 24px rgba(15, 23, 42, 0.045)",
										}}
									>
										<SoftBox display="flex" alignItems="flex-start" gap={1}>
											<SoftBox
												sx={{
													width: 30,
													height: 30,
													minWidth: 30,
													borderRadius: "50%",
													background: "linear-gradient(135deg, #EEF4FF 0%, #E7F0FF 100%)",
													display: "flex",
													justifyContent: "center",
													alignItems: "center",
												}}
											>
												<Icon sx={{ color: "#3B82F6", fontSize: "1rem" }}>south</Icon>
											</SoftBox>
											<SoftBox>
												<SoftTypography
													variant="caption"
													fontWeight="bold"
													sx={{ color: "#1E2A5A", display: "block", fontSize: { xs: "0.9rem", sm: "0.96rem" } }}
												>
													Pedidos
												</SoftTypography>
												<SoftTypography
													variant="h5"
													fontWeight="bold"
													sx={{
														color: "#1E2A5A",
														lineHeight: 1.02,
														mt: 0.18,
														letterSpacing: "-0.03em",
														fontSize: metricCardFontSize(pedidosResumenValue),
														whiteSpace: "nowrap",
														fontVariantNumeric: "tabular-nums",
													}}
												>
													{`$\u00A0${pedidosResumenValue}`}
												</SoftTypography>
												<SoftTypography
													variant="caption"
													fontWeight="bold"
													sx={{ color: "#8A9ABD", mt: 0.28, display: "block", fontSize: { xs: "0.76rem", sm: "0.82rem" } }}
												>
													{`en ${cuentaCorrienteActivitySummary.monthLabel}`}
												</SoftTypography>
											</SoftBox>
										</SoftBox>
									</Card>
								</Grid>

								<Grid item xs={6}>
									<Card
										sx={{
											p: { xs: 1.3, sm: 1.55 },
											borderRadius: "18px",
											border: "1px solid rgba(226, 232, 240, 0.95)",
											background: "rgba(255, 255, 255, 0.82)",
											boxShadow: "0 10px 24px rgba(15, 23, 42, 0.045)",
										}}
									>
										<SoftBox display="flex" alignItems="flex-start" gap={1}>
											<SoftBox
												sx={{
													width: 30,
													height: 30,
													minWidth: 30,
													borderRadius: "50%",
													background: "linear-gradient(135deg, #E9F9EF 0%, #DDF6E7 100%)",
													display: "flex",
													justifyContent: "center",
													alignItems: "center",
												}}
											>
												<Icon sx={{ color: "#16A34A", fontSize: "1rem" }}>north</Icon>
											</SoftBox>
											<SoftBox>
												<SoftTypography
													variant="caption"
													fontWeight="bold"
													sx={{ color: "#1E2A5A", display: "block", fontSize: { xs: "0.9rem", sm: "0.96rem" } }}
												>
													Pagos
												</SoftTypography>
												<SoftTypography
													variant="h5"
													fontWeight="bold"
													sx={{
														color: "#1E2A5A",
														lineHeight: 1.02,
														mt: 0.18,
														letterSpacing: "-0.03em",
														fontSize: metricCardFontSize(pagosResumenValue),
														whiteSpace: "nowrap",
														fontVariantNumeric: "tabular-nums",
													}}
												>
													{`$\u00A0${pagosResumenValue}`}
												</SoftTypography>
												<SoftTypography
													variant="caption"
													fontWeight="bold"
													sx={{ color: "#8A9ABD", mt: 0.28, display: "block", fontSize: { xs: "0.76rem", sm: "0.82rem" } }}
												>
													{`en ${cuentaCorrienteActivitySummary.monthLabel}`}
												</SoftTypography>
											</SoftBox>
										</SoftBox>
									</Card>
								</Grid>
							</Grid>
						</SoftBox>
					</Card>

					<SoftBox
						display="flex"
						alignItems="center"
						gap={1}
						flexWrap="wrap"
						sx={{ mt: 0.5 }}
					>
						{sedesCuentaCorriente.length > 1 ? (
							<SoftBox sx={{ minWidth: { xs: "100%", sm: 260 } }}>
								<Select
									size="small"
									fullWidth
									value={selectedSedeCC || ""}
									onChange={handleChangeSedeCuentaCorriente}
								>
									{sedesCuentaCorriente.map((sede) => (
										<MenuItem key={sede.id} value={Number(sede.id)}>
											{sede.nombre}
										</MenuItem>
									))}
								</Select>
							</SoftBox>
						) : null}

						{filters.map((filterItem) => {
							const isActive = cuentaCorrienteActivityFilter === filterItem.id;

							return (
								<SoftButton
									key={filterItem.id}
									circular
									color={isActive ? "info" : "white"}
									variant={isActive ? "gradient" : "contained"}
									onClick={() => setCuentaCorrienteActivityFilter(filterItem.id)}
									sx={{
										borderRadius: "16px",
										boxShadow: isActive ? "0 12px 28px rgba(8, 145, 178, 0.18)" : "none",
										color: isActive ? "#fff" : "#0F172A",
										border: isActive ? "none" : "1px solid rgba(148, 163, 184, 0.24)",
									}}
								>
									{filterItem.label}
								</SoftButton>
							);
						})}
					</SoftBox>

					{isLoading ? (
						<Card
							sx={{
								borderRadius: "28px",
								border: "1px solid rgba(148, 163, 184, 0.18)",
								p: 3,
							}}
						>
							<Loading />
						</Card>
					) : cuentaCorrienteActivityGroupedItems.length > 0 ? (
						<SoftBox display="flex" flexDirection="column" gap={2}>
							{cuentaCorrienteActivityGroupedItems.map((group) => (
								<SoftBox key={group.key} display="flex" flexDirection="column" gap={1.5}>
									<SoftTypography variant="h5" fontWeight="bold" sx={{ color: "#0F172A", px: 0.5 }}>
										{group.label}
									</SoftTypography>
									<SoftBox display="flex" flexDirection="column" gap={1.25}>
										{group.items.map((item) =>
											renderCuentaCorrienteActivityRow(item, { grouped: true })
										)}
									</SoftBox>
								</SoftBox>
							))}

							<SoftBox
								ref={cuentaCorrienteActivityLoadMoreRef}
								minHeight="48px"
								display="flex"
								justifyContent="center"
								alignItems="center"
							>
								{cuentaCorrienteActivityHasMore ? (
									<CircularProgress size={22} color="info" />
								) : (
									<SoftTypography variant="caption" sx={{ color: "#64748B" }}>
										No hay más movimientos
									</SoftTypography>
								)}
							</SoftBox>
						</SoftBox>
					) : (
						<Card
							sx={{
								borderRadius: "28px",
								border: "1px solid rgba(148, 163, 184, 0.18)",
								p: 3,
							}}
						>
							<SinDatos texto="Sin movimientos para los filtros seleccionados" />
						</Card>
					)}
				</SoftBox>
			</SoftBox>
		);
	};


	return (
		<SoftBox
			onContextMenu={handleContextMenu}
			sx={{
				userSelect: "none",
				WebkitUserSelect: "none",
				MozUserSelect: "none",
				msUserSelect: "none",
			}}
		>
			<Fade in={false} unmountOnExit>
				<SoftBox
					display="flex"
					flexDirection="column"
					justifyContent="center"
					alignItems="center"
					height="100vh"
					gap={2}
					sx={{
						backgroundColor: getUproColor("uproPink"),
					}}
				>
					<SoftBox
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<SoftBox component="img" src={upro_receso} alt="UPrO Logo" width="20rem" />
						<SoftTypography variant="h4" align="center" color="white">
							¡Hola! Nos encontramos en período de receso. Retomaremos nuestras actividades a partir del 19 de enero del 2026
						</SoftTypography>
					</SoftBox>
					<SoftButton
						circular
						color="white"
						onClick={() => { setShowCartel(false) }}
						sx={{
							color: getUproColor("uproPink"),
						}}
					>
						<Icon>arrow_back</Icon>&nbsp; Volver
					</SoftButton>
				</SoftBox>
			</Fade>
			<Fade in={!showCartel && loading || loadingCCInfo} unmountOnExit>
				<SoftBox
					display="flex"
					justifyContent="center"
					alignItems="center"
					height="100vh"
					sx={{
						backgroundColor: getUproColor("uproPink"),
					}}
				>
					<SoftBox
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<SoftBox component="img" src={logo_alt} alt="UPrO Logo" width="15rem" />
						<SoftTypography variant="h4" align="center" color="white">
							Bienvenido a UPrO Comedor
						</SoftTypography>
					</SoftBox>
				</SoftBox>
			</Fade>
			<Fade in={!showCartel && !loading && !usuario && !loadingPedidos} unmountOnExit>
				<SoftBox
					display="flex"
					justifyContent="center"
					alignItems="center"
					height="100vh"
					sx={{
						backgroundColor: getUproColor("uproPink"),
					}}
				>
					<SoftBox
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<SoftBox component="img" src={logo_alt} alt="UPrO Logo" width="15rem" />
						<SoftTypography variant="h4" align="center" color="white">
							Oops! La sesión no es válida
						</SoftTypography>
						<SoftButton
							circular
							color="white"
							onClick={() => {
								const origen = new URLSearchParams(window.location.search).get("origin");
								if (origen) {
									logout();
									window.location.href = origen;
								} else {
									logout();
									window.location.href = `${window.location.origin}/login`;
								}
							}}
							sx={{
								color: getUproColor("uproPink"),
							}}
						>
							<Icon>arrow_back</Icon>&nbsp; Volver
						</SoftButton>
					</SoftBox>
				</SoftBox>
			</Fade>
			<Fade in={!showCartel && !idSede} unmountOnExit>
				<SoftBox
					display="flex"
					justifyContent="center"
					alignItems="center"
					height="100vh"
					sx={{
						backgroundColor: getUproColor("uproPink"),
					}}
				>
					<SoftBox
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<SoftBox component="img" src={logo_alt} alt="UPrO Logo" width="15rem" />
						<SoftTypography variant="h4" align="center" color="white">
							Seleccioná una sede en la que va a realizar tu pedido
						</SoftTypography>
						<SoftBox width="80%">
							<Select
								fullWidth
								value={idSede || ""}
								onChange={(e) => {
									setIdSede(e.target.value);
									localStorage.setItem("idSede", e.target.value);
									localStorage.removeItem("selectedLocation");
									// window.location.reload();
								}}
								displayEmpty
								inputProps={{ "aria-label": "Sede" }}
							>
								<MenuItem value="" disabled>
									Seleccione una sede
								</MenuItem>
								{dataSedes?.sedesToken?.map((sede) => (
									<MenuItem key={sede.id} value={sede.id}>
										{sede.nombre}
									</MenuItem>
								))}
							</Select>
						</SoftBox>
					</SoftBox>
				</SoftBox>
			</Fade>

			{!showCartel && idSede && !loading && usuario && (
				<PageLayout>
					<SoftBox width="100%" display="flex" flexDirection="column" gap={2}>
						<SoftBox py={2} display="flex" justifyContent="space-between" alignItems="center">
							<SoftBox>
								<SoftTypography variant="h6">Bienvenido</SoftTypography>
								<SoftTypography variant="h5" fontWeight="bold">
									{makeNiceText(
										`${usuario?.decodeUser?.nombre} ${usuario?.decodeUser?.apellido}`,
										"nombre"
									)}
								</SoftTypography>
							</SoftBox>
							<SoftButton
								color="primary"
								circular
								onClick={() => {
									confirmar({
										title: "Cerrar sesión",
										message: `¿Estas seguro que querés cerrar sesión?`,
										icon: "logout",
										func: () => {
											const origen = new URLSearchParams(window.location.search).get("origin");
											if (origen) {
												logout();
												window.location.href = origen;
											} else {
												logout();
												window.location.href = `${window.location.origin}/login`;
											}
										},
									});
								}}
							>
								<Icon>logout</Icon>&nbsp; Salir
							</SoftButton>
						</SoftBox>
						{/* Seccion que permite cambiar la sede */}
						<Card>
							<SoftBox
								p={2}
								display="flex"
								justifyContent="space-between"
								alignItems="center"
								flexWrap="wrap"
								gap={2}
							>
								<SoftBox display="flex" alignItems="center" gap={2}>
									<SoftBox>
										<Card>
											<SoftBox
												bgColor={getUproColor("uproBlue")}
												sx={{
													width: "30px",
													height: "30px",
												}}
												display="flex"
												justifyContent="center"
												alignItems="center"
											>
												<Icon fontSize="small" sx={{ color: "#fff" }}>
													location_on
												</Icon>
											</SoftBox>
										</Card>
									</SoftBox>
									<SoftBox>
										<SoftTypography variant="h6" fontWeight="bold">
											Sede seleccionada:
										</SoftTypography>
									</SoftBox>
								</SoftBox>
								<SoftBox
									sx={{
										minWidth: { xs: "100%", sm: "250px" },
										maxWidth: { xs: "100%", sm: "300px" },
									}}
								>
									<Select
										fullWidth
										value={idSede || ""}
										onChange={(e) => {
											const nuevaSede = e.target.value;
											confirmar({
												title: "Cambiar sede",
												message: `¿Estás seguro que querés cambiar a la sede "${dataSedes?.sedesToken?.find((s) => s.id === nuevaSede)?.nombre
													}"?`,
												icon: "location_on",
												func: () => {
													setIdSede(nuevaSede);
													localStorage.setItem("idSede", nuevaSede);
													localStorage.removeItem("selectedLocation");

													window.location.reload();
												},
											});
										}}
										displayEmpty
										inputProps={{ "aria-label": "Sede" }}
										size="small"
									>
										<MenuItem value="" disabled>
											Seleccione una sede
										</MenuItem>
										{dataSedes?.sedesToken?.map((sede) => (
											<MenuItem key={sede.id} value={sede.id}>
												<SoftBox display="flex" alignItems="center" gap={1}>
													<Icon fontSize="small" color="primary">
														{sede.id === idSede ? "radio_button_checked" : "radio_button_unchecked"}
													</Icon>
													{sede.nombre}
												</SoftBox>
											</MenuItem>
										))}
									</Select>
								</SoftBox>
							</SoftBox>
						</Card>

						<Grid container spacing={2} alignItems="stretch">
							<Grid item xs={12} sm={3}>
								<Card sx={{ height: "100%" }}>
									<SoftBox display="flex" height="100%">
										<SoftBox p={2} display="flex" flexDirection="column" gap={1}>
											<SoftBox display="flex" alignItems="center" gap={1}>
												<SoftBox>
													<Card>
														<SoftBox
															bgColor={getUproColor("uproYellow")}
															sx={{
																width: "30px",
																height: "30px",
															}}
															display="flex"
															justifyContent="center"
															alignItems="center"
														>
															<Icon fontSize="small" sx={{ color: "#fff" }}>
																schedule
															</Icon>
														</SoftBox>
													</Card>
												</SoftBox>
												<SoftTypography variant="h6" fontWeight="bold">
													¿En qué horario puedo pedir?
												</SoftTypography>
											</SoftBox>
											<SoftTypography variant="h7" fontWeight="regular">
												{`Tenés tiempo hasta las ${mediodia?.horarioFin
													? dayjs(mediodia.horarioFin, "HH:mm:ss").format("HH:mm")
													: "12:30"
													} hs para el turno mañana y hasta las ${noche?.horarioInicio
														? dayjs(noche.horarioInicio, "HH:mm:ss").format("HH:mm")
														: "21:30"
													} hs para el turno tarde. Recordá que hay cupos limitados, ¡pedí con tiempo!`}
											</SoftTypography>
										</SoftBox>
									</SoftBox>
								</Card>
							</Grid>
							<Grid item xs={12} sm={3}>
								<Card sx={{ height: "100%" }}>
									<SoftBox display="flex" height="100%">
										<SoftBox p={2} display="flex" flexDirection="column" gap={1}>
											<SoftBox display="flex" alignItems="center" gap={1}>
												<SoftBox>
													<Card>
														<SoftBox
															bgColor={getUproColor("uproGreen")}
															sx={{
																width: "30px",
																height: "30px",
															}}
															display="flex"
															justifyContent="center"
															alignItems="center"
														>
															<Icon fontSize="small" sx={{ color: "#fff" }}>
																restaurant
															</Icon>
														</SoftBox>
													</Card>
												</SoftBox>
												<SoftTypography variant="h6" fontWeight="bold">
													¿Cuántos pedidos puedo hacer?
												</SoftTypography>
											</SoftBox>
											<SoftTypography variant="h7" fontWeight="regular">
												Podés hacer hasta 2 pedidos por día: uno para el mediodía y otro para la
												noche.
											</SoftTypography>
										</SoftBox>
									</SoftBox>
								</Card>
							</Grid>

							<Grid item xs={12} sm={3}>
								<Card sx={{ height: "100%" }}>
									<SoftBox display="flex" height="100%">
										<SoftBox p={2} display="flex" flexDirection="column" gap={1}>
											<SoftBox display="flex" alignItems="center" gap={1}>
												<SoftBox>
													<Card>
														<SoftBox
															bgColor={getUproColor("uproBlue")}
															sx={{
																width: "30px",
																height: "30px",
															}}
															display="flex"
															justifyContent="center"
															alignItems="center"
														>
															<Icon fontSize="small" sx={{ color: "#fff" }}>
																attach_money
															</Icon>
														</SoftBox>
													</Card>
												</SoftBox>
												<SoftTypography variant="h6" fontWeight="bold">
													¿Cómo pago mi pedido?
												</SoftTypography>
											</SoftBox>
											<SoftTypography variant="h7" fontWeight="regular">
												Si elegís pagar por transferencia, acordate de subir el comprobante para
												confirmar tu pedido. Si no subís el comprobante de transferencia dentro de
												los 30 minutos posteriores a hacer el pedido, se anulará
											</SoftTypography>
										</SoftBox>
									</SoftBox>
								</Card>
							</Grid>

							<Grid item xs={12} sm={3}>
								<Card sx={{ height: "100%" }}>
									<SoftBox display="flex" height="100%">
										<SoftBox p={2} display="flex" flexDirection="column" gap={1}>
											<SoftBox display="flex" alignItems="center" gap={1}>
												<SoftBox>
													<Card>
														<SoftBox
															bgColor={getUproColor("uproPink")}
															sx={{
																width: "30px",
																height: "30px",
															}}
															display="flex"
															justifyContent="center"
															alignItems="center"
														>
															<Icon fontSize="small" sx={{ color: "#fff" }}>
																info
															</Icon>
														</SoftBox>
													</Card>
												</SoftBox>
												<SoftTypography variant="h6" fontWeight="bold">
													¿Qué pasa si no hice mi pedido a tiempo?
												</SoftTypography>
											</SoftBox>
											{/* <SoftTypography variant="h7" fontWeight="regular">
                                                Si no hiciste tu pedido a tiempo, podés solicitar un menú en el comedor,
                                                sujeto a disponibilidad. Valor: ${dataPrecioParticular?.preciosMenuLibre || "Consultar"}
                                            </SoftTypography> */}
											<SoftTypography variant="h7" fontWeight="regular">
												{`Si no hiciste tu pedido a tiempo, podés solicitar un menú en el comedor,
                                                sujeto a disponibilidad. ${dataPrecioParticular?.preciosMenuLibre
														? "Valor: " +
														formatMoneyPunto(
															dataPrecioParticular?.preciosMenuLibre
														)
														: "Consultar"
													}`}
											</SoftTypography>
										</SoftBox>
									</SoftBox>
								</Card>
							</Grid>
						</Grid>
						<CalendarioMenusSemana token={token} idSede={idSede} />
						{usuario?.decodeUser?.habilitadoCuentaCorriente && renderCuentaCorrienteActivityCard()}
						{renderVouchersBeneficiosCard()}
						<SoftBox>
							<Card
								sx={{
									display: {
										xs: "none",
										sm: "block",
									},
									border: "1px solid rgba(0, 0, 0, 0.12)",
								}}
							>
								<SoftBox
									display="flex"
									justifyContent="space-between"
									px={2}
									py={1}
									pt={{
										xs: 2,
										sm: 1,
									}}
									gap={2}
									flexDirection={{
										xs: "column",
										sm: "row",
									}}
									sx={{
										borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
										cursor: "pointer",
										"&:hover": {
											backgroundColor: "rgba(0, 0, 0, 0.04)",
										},
									}}
								>
									<SoftBox display="flex" alignItems="center">
										<SoftBox mr={1} display="flex" alignItems="center">
											<Icon color="primary">receipt</Icon>
										</SoftBox>
										<SoftTypography variant="h6" fontWeight="bold">
											Pedidos realizados
										</SoftTypography>
									</SoftBox>
									<SoftBox
										display="flex"
										justifyContent="flex-end"
										alignItems="center"
										gap={2}
										py={1}
									>
										<SoftBox
											sx={{
												width: {
													xs: "100%",
													sm: "auto",
												},
											}}
										>
											<Select
												label="Estado"
												value={filter?.estado || -1}
												onChange={(e) => {
													setFilter({
														...filter,
														estado: e.target.value === -1 ? undefined : e.target.value,
													});
													setPage(1);
												}}
											>
												<MenuItem value={-1}>
													<SoftBox display="flex" alignItems="center">
														<Icon sx={{ color: getUproColor("uproPink") }} fontSize="small">
															filter_list
														</Icon>{" "}
														&nbsp; Todos
													</SoftBox>
												</MenuItem>
												<MenuItem value="RESERVADO">
													<SoftBox display="flex" alignItems="center">
														<Icon sx={{ color: getUproColor("uproYellow") }} fontSize="small">
															receipt
														</Icon>{" "}
														&nbsp; Reservados
													</SoftBox>
												</MenuItem>
												<MenuItem value="CERRADO">
													<SoftBox display="flex" alignItems="center">
														<Icon sx={{ color: getUproColor("uproGreen") }} fontSize="small">
															done
														</Icon>{" "}
														&nbsp; Cerrados
													</SoftBox>
												</MenuItem>
												<MenuItem value="ANULADO">
													<SoftBox display="flex" alignItems="center">
														<Icon sx={{ color: getUproColor("uproBlue") }} fontSize="small">
															cancel
														</Icon>{" "}
														&nbsp; Anulados
													</SoftBox>
												</MenuItem>
											</Select>
										</SoftBox>
										<SoftBox
											display={{
												xs: "none",
												sm: "flex",
											}}
										>
											<Tooltip
												title={
													segmentoCuentaCorrienteBloqueaNuevoPedido
														? `No podés generar pedidos mientras estás viendo el segmento ${getCuentaCorrienteSegmentoLabel(
															segmentoCuentaCorrienteSeleccionado
														).toLowerCase()}.`
														: "Nuevo pedido"
												}
												placement="top"
											>
												<SoftButton
													color="uproGreen"
													circular
													onClick={() => setOpenModalNuevoPedido(true)}
													disabled={segmentoCuentaCorrienteBloqueaNuevoPedido}
												>
													<Icon>add_shopping_cart</Icon>
													&nbsp; Nuevo pedido
												</SoftButton>
											</Tooltip>
										</SoftBox>
									</SoftBox>
								</SoftBox>
								<Collapse in={!loading && !loadingPedidos}>
									<SoftBox
										sx={{
											"& .MuiTableRow-root:not(:last-child)": {
												"& td": {
													borderBottom: ({ borders: { borderWidth, borderColor } }) =>
														`${borderWidth[1]} solid ${borderColor}`,
												},
											},
										}}
									>
										{loading || loadingPedidos ? (
											<Loading />
										) : rows?.length > 0 ? (
											<DataTable
												columns={columns}
												rows={rows}
												order={orderBy}
												setOrder={() => { }}
											/>
										) : (
											<SinDatos />
										)}
									</SoftBox>
								</Collapse>
								<Collapse in={!loading && !loadingPedidos && rows?.length > 0}>
									<CustomPagination
										loading={loading}
										length={data?.misPedidosPublic.length}
										total={pagesInfo?.total}
										pages={pagesInfo?.pages}
										page={page}
										setPage={setPage}
										noCard
									/>
								</Collapse>
							</Card>
							<Card
								sx={{
									display: {
										xs: "block",
										sm: "none",
									},
								}}
							>
								<SoftBox display="flex" flexDirection="column" gap={2}>
									<SoftBox p={2} pb={0}>
										<SoftBox display="flex" alignItems="center">
											<SoftBox mr={1} display="flex" alignItems="center">
												<Icon color="primary">receipt</Icon>
											</SoftBox>
											<SoftTypography variant="h6" fontWeight="bold">
												Pedidos realizados
											</SoftTypography>
										</SoftBox>
									</SoftBox>
									<SoftBox px={2}>
										<Tooltip
											title={
												segmentoCuentaCorrienteBloqueaNuevoPedido
													? `No podés generar pedidos mientras estás viendo el segmento ${getCuentaCorrienteSegmentoLabel(
														segmentoCuentaCorrienteSeleccionado
													).toLowerCase()}.`
													: "Nuevo pedido"
											}
											placement="top"
										>
											<SoftButton
												color="uproGreen"
												circular
												fullWidth
												onClick={() => setOpenModalNuevoPedido(true)}
												disabled={segmentoCuentaCorrienteBloqueaNuevoPedido}
											>
												<Icon>add_shopping_cart</Icon>
												&nbsp; Nuevo pedido
											</SoftButton>
										</Tooltip>
									</SoftBox>

									{loading || loadingPedidos ? (
										<SoftBox mt={-2}>
											<Loading />
										</SoftBox>
									) : data?.misPedidosPublic?.length > 0 ? (
										<SoftBox display="flex" flexDirection="column" gap={2} p={2} pt={0}>
											{data?.misPedidosPublic?.map((pedido) => {
												return (
													<Card
														key={pedido.id}
														sx={{
															cursor: "pointer",
															border: "1px solid rgba(0, 0, 0, 0.12)",

															"&:hover": {
																backgroundColor: "rgba(0, 0, 0, 0.04)",
															},
														}}
													>
														<SoftBox
															p={2}
															display="flex"
															flexDirection="column"
															gap={2}
															onClick={() => {
																setSelectedPedido(pedido);
																setOpenModalPedidoDetalle(true);
															}}
														>
															<SoftBox
																display="flex"
																justifyContent="space-between"
																alignItems="center"
															>
																<SoftBox
																	display="flex"
																	alignItems="center"
																	gap={1}
																	sx={{
																		width: "100%",
																	}}
																>
																	<Card
																		sx={{
																			width: "35px",
																			height: "35px",
																			minWidth: "35px",
																			maxWidth: "35px",
																			flexShrink: 0,
																		}}
																	>
																		<SoftBox
																			bgColor={
																				pedido?.estado === "ABIERTO"
																					? getUproColor("primary")
																					: pedido?.estado === "CERRADO"
																						? getUproColor("uproGray")
																						: pedido?.estado === "ANULADO"
																							? getUproColor("uproBlue")
																							: pedido?.estado === "RESERVADO" &&
																								dayjs(pedido?.fechaPedido).isAfter(
																									dayjs().startOf("day")
																								)
																								? getUproColor("uproGreen")
																								: getUproColor("uproYellow")
																			}
																			sx={{
																				width: "35px",
																				height: "35px",
																			}}
																			display="flex"
																			justifyContent="center"
																			alignItems="center"
																		>
																			<Icon fontSize="small" sx={{ color: "#fff" }}>
																				{pedido.tipo === "Cafetería" ? "coffee" : "restaurant"}
																			</Icon>
																		</SoftBox>
																	</Card>
																	<SoftTypography variant="h6" fontWeight="bold">
																		Pedido #{pedido.id}
																	</SoftTypography>
																</SoftBox>
																<SoftBadge
																	container
																	variant="color"
																	color={
																		pedido?.estado === "ABIERTO"
																			? getUproColor("primary")
																			: pedido?.estado === "CERRADO"
																				? getUproColor("uproGray")
																				: pedido?.estado === "ANULADO"
																					? getUproColor("uproBlue")
																					: pedido?.estado === "RESERVADO" &&
																						dayjs(pedido?.fechaPedido).isAfter(dayjs().startOf("day"))
																						? getUproColor("uproGreen")
																						: getUproColor("uproYellow")
																	}
																	badgeContent={
																		pedido?.estado === "RESERVADO" &&
																			dayjs(pedido?.fechaPedido).isBefore(dayjs().startOf("day"))
																			? "Reserva vencida"
																			: pedido?.estado === "RESERVADO" &&
																				pedido?.venta?.pagos[0]?.medioDePago?.nombre !=
																				"Transferencia"
																				? `Reservado para ${dayjs(pedido?.fechaPedido).isAfter(
																					dayjs().subtract(2, "day")
																				)
																					? formatDate(pedido?.fechaPedido, "XXXX")
																					: formatDate(pedido?.fechaPedido, "dd/MM/yyyy")
																				}`
																				: pedido?.estado === "RESERVADO" &&
																					pedido?.venta?.pagos[0]?.medioDePago?.nombre ==
																					"Transferencia" &&
																					pedido?.venta?.pagos[0]?.estado == "Pendiente"
																					? `Pendiente de pago`
																					: pedido?.estado === "RESERVADO" &&
																						pedido?.venta?.pagos[0]?.medioDePago?.nombre ==
																						"Transferencia" &&
																						pedido?.venta?.pagos[0]?.estado == "Revision"
																						? `Pago en revisión`
																						: pedido?.estado
																	}
																/>
															</SoftBox>
															<SoftBox display="flex" alignItems="center" gap={2}>
																<SoftBox display="flex" alignItems="center" gap={1}>
																	<Icon color="primary" fontSize="small">
																		calendar_today
																	</Icon>
																	<SoftTypography variant="h7">
																		{dayjs(pedido.fechaPedido).format("DD/MM/YYYY")}
																	</SoftTypography>
																</SoftBox>
																<SoftBox display="flex" alignItems="center" gap={1}>
																	<Icon color="primary" fontSize="small">
																		access_time
																	</Icon>
																	<SoftTypography variant="h7">
																		{dayjs(pedido.horaReserva).format("HH:mm")}
																	</SoftTypography>
																</SoftBox>
																<SoftBox display="flex" alignItems="center" gap={1}>
																	<Icon color="primary" fontSize="small">
																		attach_money
																	</Icon>
																	<SoftTypography variant="h7">
																		{formatMoneyPunto(pedido.total)}
																	</SoftTypography>
																</SoftBox>
															</SoftBox>
														</SoftBox>
														<SoftBox display="flex" alignItems="center" gap={1} p={2}>
															<SoftButton
																color="uproYellow"
																circular
																fullWidth
																onClick={() => {
																	setSelectedPedido(pedido);
																	setOpenModalValorarPedido(true);
																}}
																disabled={pedido?.valoracion}
															>
																<Icon>star</Icon>
																&nbsp; Valorar pedido
															</SoftButton>
														</SoftBox>
													</Card>
												);
											})}
											<Collapse in={data?.misPedidosPublic?.length > 0}>
												<SoftBox display="flex" justifyContent="center" alignItems="center" p={1}>
													<SoftTypography variant="h7">
														Mostrando {data?.misPedidosPublic?.length} de {pagesInfo?.total} pedidos
													</SoftTypography>
												</SoftBox>
											</Collapse>
											<Collapse
												in={data?.misPedidosPublic?.length > 0 && limit < pagesInfo?.total}
												unmountOnExit
												mountOnEnter
											>
												<SoftButton
													color="primary"
													circular
													fullWidth
													onClick={() => {
														setLimit(limit + 10);
													}}
												>
													Cargar más pedidos&nbsp;
													<Icon>expand_more</Icon>
												</SoftButton>
											</Collapse>
										</SoftBox>
									) : (
										<SinDatos />
									)}
								</SoftBox>
							</Card>
						</SoftBox>
						{renderVouchersDetallesOverlay()}
						{renderVoucherConsumosOverlay()}
						{renderCuentaCorrienteActivityOverlay()}
						<DialogVouchersActivos
							open={canShowVouchersActivosDialog && openVouchersActivosDialog}
							onAcknowledge={handleAcknowledgeVoucherNotifications}
							acknowledging={reconociendoNotificacionesVouchers}
							errorMessage={voucherAcknowledgementError}
							vouchers={vouchersActivosCliente}
						/>
						<ModalNuevoPedido
							open={openModalNuevoPedido}
							handleClose={() => setOpenModalNuevoPedido(false)}
							refetch={refrescarSistemaComedorPostAlta}
							onPedidoCreado={refrescarSistemaComedorPostAlta}
							deshabilitar={segmentoCuentaCorrienteBloqueaNuevoPedido}
							pedidosHoy={pedidosHoy}
							token={token}
							horariosMenu={horariosMenu}
							usuario={usuario?.decodeUser}
							vouchersActivos={vouchersActivosCliente}
							loadingVouchersActivos={loadingVouchersActivosCliente}
							onActualizarVouchers={refetchVouchersActivosCliente}
							saldoCuentaCorrientePendienteActual={saldoCuentaCorrientePendienteActual}
							limiteCuentaCorriente={limiteCuentaCorrienteActual}
							bloqueoCuentaCorrienteLimite={bloqueoCuentaCorrienteLimite}
						/>
						<ModalDetallesPedido
							open={openModalPedidoDetalle}
							handleClose={() => {
								setOpenModalPedidoDetalle(false);
								setSelectedPedido(null);
							}}
							idPedido={selectedPedido?.id}
							token={token}
							refetch={() => {
								getPedidos({
									variables: {
										app: usuario.decodeUser.sistema,
										idUser: usuario.decodeUser.id,
										dni: usuario.decodeUser.dni,
										limit,
										token,
									},
								});
							}}
						/>
						<ModalValorarPedido
							open={openModalValorarPedido}
							handleClose={() => {
								setOpenModalValorarPedido(false);
								setSelectedPedido(null);
							}}
							idPedido={selectedPedido?.id}
							token={token}
							refetch={() => {
								getPedidos({
									variables: {
										app: usuario.decodeUser.sistema,
										idUser: usuario.decodeUser.id,
										dni: usuario.decodeUser.dni,
										limit,
										token,
									},
								});
							}}
						/>
						<ModalCargarPagoCC
							open={modalCargarPagoCC}
							montoSugerido={
								saldoCuentaCorrienteUnificado < 0 ? Math.abs(saldoCuentaCorrienteUnificado) : 0
							}
							usuario={usuario?.decodeUser}
							refetchCC={refrescarSistemaComedorPostAlta}
							onClose={() => setOpenModalCargarPagoCC(false)}
							dataSedes={dataSedesInfo || []}
							token={token}
						/>
						<Modal
							open={openModalFactura}
							onClose={handleCloseFacturaModal}
							closeAfterTransition
							aria-labelledby="modal-factura-title"
							aria-describedby="modal-factura-description"
						>
							<Fade in={openModalFactura}>
								<SoftBox
									sx={{
										position: "absolute",
										top: "50%",
										left: "50%",
										transform: "translate(-50%, -50%)",
										width: "80%",
										maxHeight: "90%",
										overflowY: "auto",
										bgcolor: "background.paper",
										boxShadow: 24,
										p: 4,
									}}
								>
									<Card>
										{facturaData && empresaData && (
											<FacturaImpresion factura={facturaData} empresa={empresaData} />
										)}
									</Card>
								</SoftBox>
							</Fade>
						</Modal>
						<Modal
							open={Boolean(previewFileCC)}
							onClose={() => setPreviewFileCC(null)}
							closeAfterTransition
							sx={{ zIndex: 1700 }}
						>
							<Fade in={Boolean(previewFileCC)}>
								<SoftBox
									sx={{
										position: "fixed",
										inset: 0,
										width: "100vw",
										height: "100vh",
										bgcolor: "#F8FAFC",
										boxShadow: 24,
										zIndex: 1701,
										display: "flex",
										flexDirection: "column",
									}}
								>
									<SoftBox
										display="flex"
										justifyContent="space-between"
										alignItems="center"
										px={2}
										py={1.5}
										sx={{
											borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
											background: "rgba(255, 255, 255, 0.9)",
											backdropFilter: "blur(12px)",
										}}
									>
										<SoftTypography variant="h6">Comprobante</SoftTypography>
										<SoftButton
											color="white"
											circular
											iconOnly
											onClick={() => setPreviewFileCC(null)}
											sx={{
												color: "#0F172A",
												boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
											}}
										>
											<Icon>close</Icon>
										</SoftButton>
									</SoftBox>
									<SoftBox
										sx={{
											flex: 1,
											minHeight: 0,
											p: { xs: 1, sm: 2 },
										}}
									>
										{previewFileCC ? (
											isImageFile(previewFileCC) ? (
												<SoftBox
													sx={{
														width: "100%",
														height: "100%",
														display: "flex",
														justifyContent: "center",
														alignItems: "center",
														background: "#E2E8F0",
														borderRadius: 2,
														overflow: "hidden",
													}}
												>
													<img
														src={resolveStoredFileUrl(previewFileCC)}
														alt="Comprobante"
														style={{
															maxWidth: "100%",
															maxHeight: "100%",
															objectFit: "contain",
														}}
													/>
												</SoftBox>
											) : (
												<iframe
													src={resolveStoredFileUrl(previewFileCC)}
													style={{ width: "100%", height: "100%", border: "none", borderRadius: 8 }}
													title="Comprobante"
												/>
											)
										) : null}
									</SoftBox>
								</SoftBox>
							</Fade>
						</Modal>
					</SoftBox>
				</PageLayout>
			)}
		</SoftBox>
	);
}

export default function SistemaComedorConBloqueo() {
	return <BloqueoAutogestion token={captureAppToken()}><SistemaComedor /></BloqueoAutogestion>;
}
